/**
 * Zuntly Brain - AI Command Console
 * ---------------------------------
 * Type natural commands and the Brain turns them into one-step actions.
 *
 * Inventory Mode (live):
 *   e.g. "Update Peasaaarapippu 50" -> the Brain finds the right product even
 *   when misspelled, shows a single confirm card (product / branch / option /
 *   quantity) and applies the update, recording it as a manual inventory log.
 *
 * More modes (Orders, Products, Analytics) are wired to land next.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaBoxes,
  FaSpinner,
  FaCheckCircle,
  FaSearch,
  FaArrowRight,
  FaWarehouse,
  FaStore,
  FaTruck,
  FaPlus,
  FaTimes,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaClock,
  FaChartLine
} from 'react-icons/fa';
import { IoSend } from 'react-icons/io5';
import { toast } from 'react-toastify';
import {
  brainGetExamples,
  brainGetDailyUsage,
  brainResolveInventory,
  brainExecuteInventory,
  brainExecuteDistribution,
  brainSearchProducts,
  brainBuildLines,
  brainGetExpiryExamples,
  brainResolveExpiry,
  brainExecuteExpiry
} from '../services/ClientService';
import zuntlyLogo from '../Zuntly_logo.png';

const BrainLogo = ({ className = 'w-6 h-6', alt = 'Zuntly', rounded = 'rounded-md' }) => (
  <img
    src={zuntlyLogo}
    alt={alt}
    className={`block object-contain select-none ${rounded} ${className}`}
    draggable={false}
  />
);

// ------------------------------------------------------------------
// Modes
// ------------------------------------------------------------------
const MODES = [
  { id: 'inventory', label: 'Inventory', icon: <FaBoxes />, live: true },
  { id: 'expiry', label: 'Expiry', icon: <FaCalendarAlt />, live: true },
  { id: 'orders', label: 'Orders', icon: <FaStore />, live: false },
  { id: 'products', label: 'Products', icon: <FaBoxes />, live: false },
  { id: 'analytics', label: 'Analytics', icon: <FaChartLine />, live: false }
];

const BRAIN_MODE_STORAGE_KEY = 'zuntlyBrainMode';
const BRAIN_LIVE_MODES = new Set(['inventory', 'expiry']);

const readStoredBrainMode = () => {
  try {
    const stored = localStorage.getItem(BRAIN_MODE_STORAGE_KEY);
    if (stored && BRAIN_LIVE_MODES.has(stored)) return stored;
  } catch {
    /* ignore storage errors */
  }
  return 'inventory';
};

const persistBrainMode = (nextMode) => {
  try {
    localStorage.setItem(BRAIN_MODE_STORAGE_KEY, nextMode);
  } catch {
    /* ignore storage errors */
  }
};

const FALLBACK_EXPIRY_HINTS = {
  examples: [
    'Set expiry product one 500gm 31/12/2026',
    'What products expire in 10 days?',
    'Show expired products',
    'Clear expiry product one 500gm'
  ],
  placeholder: 'e.g. Set expiry product name 500gm 31/12/2026'
};

const FALLBACK_CATALOG_HINTS = {
  examples: [
    'Add product one, product two 500gm200gm 10 branch name',
    'Update product one 500gm 10 branch name',
    'Remove product one 500gm 5 branch name',
    'Distribute product one, product two 500gm 10 branch name',
    'What is the stock of product one?',
    'Check product two at branch name branch'
  ],
  placeholder: 'e.g. Add product name 10 or Remove product name 5',
  distributeDraftPlaceholder: 'Add more options or products, e.g. + 500gm 10',
  updateDraftPlaceholder: 'Add more products to update, e.g. product name 20',
  singleDraftPlaceholder: 'Add or remove options, e.g. + 500gm, disable 200gm',
  multiChatHint: 'Add product name 500gm 10'
};

const isDistributeKeyword = (text) => /\b(distribut\w*|distrib\w*)\b/i.test(text || '');

/** Short card amend messages (+ 500gm, remove 200gm) — not a fresh full command. */
const isDraftFollowUpCommand = (text) => {
  const msg = (text || '').trim().replace(/^\++\s*/, '').trim();
  if (!msg || msg.length > 120) return false;
  if (/\bbranch\b/i.test(msg)) return false;
  if (/,/.test(msg) && msg.split(',').filter((s) => s.trim().length > 3).length >= 2) return false;

  const hasWeight = /\d+(?:\.\d+)?\s*(?:kg|gm|g|ml|l|gram|ltr)\b/i.test(msg)
    || /\b\d+(?:\.\d+)?(?:kg|gm|g|ml|l)\b/i.test(msg);

  if (/^(?:remove|remov|disable|uncheck|deselect|enable|add|\+\s*)\s/i.test(msg)) {
    if (hasWeight) return true;
    if (/^(?:remove|remov|disable|uncheck|deselect)\s/i.test(msg) && msg.split(/\s+/).length <= 4) return true;
  }
  if (/^(?:\+\s*)?\d+(?:\.\d+)?\s*(?:kg|gm|g|ml|l|gram|ltr)/i.test(msg) && msg.split(/\s+/).length <= 5) return true;
  return false;
};

const isFreshInventoryCommand = (text) => {
  const msg = (text || '').trim();
  if (isDraftFollowUpCommand(msg)) return false;
  return /^(update|updte|add|remove|deduct|distribute|distribution|distrib|what|how many|how much|check|show|stock of|stock for)\b/i.test(msg);
};
const isUpdateKeyword = (text) => /\b(updat\w*|updte|upadte|set|change|add|restock|adjust|remove|deduct|reduce)\b/i.test(text || '');

const computePreviewQuantity = (branchStock, qty, quantityMode) => {
  const stock = branchStock ?? 0;
  if (quantityMode === 'add_each') return stock + qty;
  if (quantityMode === 'remove_each') return Math.max(0, stock - qty);
  return qty;
};

const getActionLabels = (quantityMode, inventoryAction) => {
  const action = inventoryAction
    || (quantityMode === 'add_each' ? 'add' : quantityMode === 'remove_each' ? 'remove' : 'update');
  if (action === 'add') {
    return {
      action: 'add',
      verb: 'Add',
      qtyLabel: 'Quantity to add',
      modeHint: 'add to existing',
      confirmVerb: 'Add',
      successTitle: 'Stock added'
    };
  }
  if (action === 'remove') {
    return {
      action: 'remove',
      verb: 'Remove',
      qtyLabel: 'Quantity to remove',
      modeHint: 'remove from branch',
      confirmVerb: 'Remove',
      successTitle: 'Stock removed'
    };
  }
  return {
    action: 'update',
    verb: 'Update',
    qtyLabel: 'New quantity',
    modeHint: 'set level',
    confirmVerb: 'Update',
    successTitle: 'Inventory updated'
  };
};

const RemoveDispositionPanel = ({
  branchName,
  warehouseBranchName,
  lineCount,
  removeDisposition,
  onSelect,
  onBack
}) => (
  <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-3 space-y-3">
    <p className="text-sm font-semibold text-amber-900">Where should removed stock go?</p>
    <p className="text-xs text-amber-800">
      Removing <b>{lineCount}</b> line(s) from <b>{branchName}</b>. Choose what happens to the stock.
    </p>
    <label className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
      removeDisposition === 'return_to_warehouse' ? 'border-emerald-400 bg-white ring-1 ring-emerald-200' : 'border-gray-200 bg-white hover:border-emerald-200'
    }`}>
      <input
        type="radio"
        name="removeDisposition"
        className="mt-0.5"
        checked={removeDisposition === 'return_to_warehouse'}
        onChange={() => onSelect('return_to_warehouse')}
      />
      <div>
        <span className="text-sm font-medium text-gray-800">Return to warehouse</span>
        <p className="text-[11px] text-gray-500">
          Add removed quantity back to <b>{warehouseBranchName || 'warehouse'}</b>
        </p>
      </div>
    </label>
    <label className={`flex items-start gap-2.5 p-2.5 rounded-lg border cursor-pointer transition-all ${
      removeDisposition === 'damaged' ? 'border-red-400 bg-white ring-1 ring-red-200' : 'border-gray-200 bg-white hover:border-red-200'
    }`}>
      <input
        type="radio"
        name="removeDisposition"
        className="mt-0.5"
        checked={removeDisposition === 'damaged'}
        onChange={() => onSelect('damaged')}
      />
      <div>
        <span className="text-sm font-medium text-gray-800">Mark as damaged / expired</span>
        <p className="text-[11px] text-gray-500">Write off stock — not added back to warehouse</p>
      </div>
    </label>
    <button type="button" onClick={onBack} className="text-xs text-gray-500 hover:text-gray-700">
      ← Back to quantities
    </button>
  </div>
);

const mergeLineItems = (existing = [], incoming = []) => {
  const map = new Map();
  for (const line of existing) map.set(line.key, line);
  for (const line of incoming) map.set(line.key, line);
  return [...map.values()];
};

const buildDistributionItemsFromGroups = (productGroups = []) => {
  const items = [];
  for (const g of productGroups) {
    for (const l of g.optionLines || []) {
      if (!l.enabled || l.quantity === '' || isNaN(l.quantity) || +l.quantity <= 0) continue;
      items.push({
        key: `${g.productId}_${l.optionId}`,
        productId: g.productId,
        productOptionId: l.optionId,
        productName: g.productName,
        optionLabel: l.label,
        quantity: +l.quantity,
        commonInventory: false,
        selectedMetric: null,
        metricId: null,
        warehouseAvailable: l.warehouseAvailable ?? 0,
        insufficient: l.insufficient ?? ((l.warehouseAvailable ?? 0) < +l.quantity)
      });
    }
  }
  return items;
};

const mergeSearchLinesIntoGroups = (groups = [], newLines = []) => {
  const gMap = new Map(groups.map((g) => [g.productId, { ...g, optionLines: [...(g.optionLines || [])] }]));
  for (const line of newLines) {
    if (line.commonInventory) continue;
    let g = gMap.get(line.productId);
    if (!g) {
      g = {
        productId: line.productId,
        productName: line.productName,
        commonInventory: false,
        optionLines: []
      };
      gMap.set(line.productId, g);
    }
    const lineMap = new Map((g.optionLines || []).map((l) => [l.optionId, { ...l }]));
    const prev = lineMap.get(line.productOptionId);
    lineMap.set(line.productOptionId, {
      optionId: line.productOptionId,
      label: line.optionLabel,
      enabled: true,
      quantity: String(line.quantity),
      warehouseAvailable: line.warehouseAvailable ?? prev?.warehouseAvailable ?? 0,
      insufficient: (line.warehouseAvailable ?? prev?.warehouseAvailable ?? 0) < +line.quantity
    });
    g.optionLines = [...lineMap.values()];
  }
  return [...gMap.values()];
};

const mergeSearchProductIntoGroups = (groups = [], product, newLines = []) => {
  if (!product?.productId) return mergeSearchLinesIntoGroups(groups, newLines);
  const addedMap = new Map(newLines.map((l) => [l.productOptionId, l]));
  const prev = groups.find((g) => g.productId === product.productId);
  const prevLineMap = new Map((prev?.optionLines || []).map((l) => [l.optionId, l]));

  const optionLines = (product.options || []).map((o) => {
    const added = addedMap.get(o.optionId);
    const old = prevLineMap.get(o.optionId);
    if (added) {
      return {
        optionId: o.optionId,
        label: o.label,
        enabled: true,
        quantity: String(added.quantity),
        warehouseAvailable: added.warehouseAvailable ?? old?.warehouseAvailable ?? 0,
        insufficient: added.insufficient ?? false
      };
    }
    if (old) return old;
    return {
      optionId: o.optionId,
      label: o.label,
      enabled: false,
      quantity: '',
      warehouseAvailable: 0,
      insufficient: false
    };
  });

  const gMap = new Map(groups.map((g) => [g.productId, g]));
  gMap.set(product.productId, {
    productId: product.productId,
    productName: product.productName,
    commonInventory: false,
    optionLines
  });
  return [...gMap.values()];
};

// ------------------------------------------------------------------
// Search & manually add products with option pick
// ------------------------------------------------------------------
const BrainProductSearchAdd = ({ branchId, quantityMode, warehouseBranchId, mode = 'update', onAdd, defaultQuantity = 10 }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [optionRows, setOptionRows] = useState([]);
  const [defaultQty, setDefaultQty] = useState(String(defaultQuantity));
  const [adding, setAdding] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  const isDist = mode === 'distribute';
  const ringClass = isDist ? 'focus:ring-indigo-400' : 'focus:ring-emerald-400';
  const btnClass = isDist ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-emerald-600 hover:bg-emerald-700';
  const activeRow = isDist ? 'border-indigo-300 bg-indigo-50/80' : 'border-emerald-300 bg-emerald-50/80';
  const chipOn = isDist ? 'border-indigo-400 bg-indigo-100 text-indigo-800' : 'border-emerald-400 bg-emerald-100 text-emerald-800';

  useEffect(() => {
    setDefaultQty(String(defaultQuantity));
  }, [defaultQuantity]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (picked) {
      setResults([]);
      return undefined;
    }
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      return undefined;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await brainSearchProducts({ search: term, limit: 12 });
        setResults(res.data?.products || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 280);
    return () => clearTimeout(debounceRef.current);
  }, [query, picked]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setResults([]);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const initOptionRows = (product, qty = defaultQuantity) => (product.options || []).map((o) => ({
    optionId: o.optionId,
    label: o.label,
    selected: product.options.length === 1,
    added: false,
    quantity: String(qty)
  }));

  const pickProduct = (product) => {
    setPicked(product);
    setDefaultQty(String(defaultQuantity));
    setOptionRows(initOptionRows(product, defaultQuantity));
    setResults([]);
    setQuery('');
  };

  const closePicker = () => {
    setPicked(null);
    setOptionRows([]);
  };

  const handleDefaultQtyChange = (raw) => {
    setDefaultQty(raw);
    setOptionRows((prev) => prev.map((r) => {
      if (r.added || !r.selected) return r;
      return { ...r, quantity: raw };
    }));
  };

  const toggleRow = (optionId) => {
    setOptionRows((prev) => prev.map((r) => {
      if (r.optionId !== optionId || r.added) return r;
      const selected = !r.selected;
      return { ...r, selected, quantity: selected ? defaultQty : r.quantity };
    }));
  };

  const updateRowQty = (optionId, raw) => {
    setDefaultQty(raw);
    setOptionRows((prev) => prev.map((r) => {
      if (r.added) return r;
      if (r.optionId === optionId) {
        return { ...r, quantity: raw, selected: raw !== '' ? true : r.selected };
      }
      if (r.selected) {
        return { ...r, quantity: raw };
      }
      return r;
    }));
  };

  const selectAllPending = () => {
    setOptionRows((prev) => prev.map((r) => (
      r.added ? r : { ...r, selected: true, quantity: defaultQty }
    )));
  };

  const pendingRows = optionRows.filter((r) => r.selected && !r.added && r.quantity !== '' && !isNaN(r.quantity) && +r.quantity > 0);
  const pendingCount = pendingRows.length;

  const handleAdd = async () => {
    if (!picked || !branchId) {
      toast.error('Select a branch first');
      return;
    }

    if (picked.isCommonInventory) {
      const qty = parseFloat(defaultQty);
      if (isNaN(qty) || qty <= 0) {
        toast.error('Enter a valid quantity');
        return;
      }
      setAdding(true);
      try {
        const res = await brainBuildLines({
          branchId,
          warehouseBranchId,
          quantity: qty,
          quantityMode,
          mode,
          selections: [{
            productId: picked.productId,
            optionIds: (picked.options || []).map((o) => o.optionId),
            quantity: qty
          }]
        });
        if (res.code === 200 && res.success && res.data?.items?.length) {
          onAdd(res.data.items, { product: picked });
          toast.success('Added to list');
          closePicker();
        } else {
          toast.error(res.message || 'Could not add product');
        }
      } catch (e) {
        toast.error(e?.message || 'Could not add product');
      } finally {
        setAdding(false);
      }
      return;
    }

    if (!pendingCount) {
      toast.error('Select at least one option that is not already added');
      return;
    }

    setAdding(true);
    try {
      const selections = pendingRows.map((r) => ({
        productId: picked.productId,
        optionIds: [r.optionId],
        quantity: parseFloat(r.quantity)
      }));
      const res = await brainBuildLines({
        branchId,
        warehouseBranchId,
        quantityMode,
        mode,
        selections
      });
      if (res.code === 200 && res.success && res.data?.items?.length) {
        onAdd(res.data.items, { product: picked });
        const addedIds = new Set(pendingRows.map((r) => r.optionId));
        setOptionRows((prev) => prev.map((r) => (
          addedIds.has(r.optionId)
            ? { ...r, added: true, selected: false }
            : r
        )));
        toast.success(`Added ${res.data.items.length} option(s). Select more variants or click Done.`);
      } else {
        toast.error(res.message || 'Could not add product');
      }
    } catch (e) {
      toast.error(e?.message || 'Could not add product');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div ref={wrapRef} className={isDist
      ? 'rounded-xl border border-dashed border-indigo-200 bg-indigo-50/20 p-3 space-y-2.5'
      : 'rounded-xl border border-dashed border-emerald-200 bg-emerald-50/20 p-3 space-y-2.5'}>
      <p className="text-xs font-medium text-gray-600 flex items-center gap-1.5">
        <FaSearch className="text-gray-400 text-[10px]" />
        Search & add product directly
      </p>

      {!picked ? (
        <div className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Type product name…"
            className={`w-full pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${ringClass} bg-white`}
          />
          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
          {searching && <FaSpinner className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs animate-spin" />}
          {results.length > 0 && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
              {results.map((p) => (
                <button
                  key={p.productId}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickProduct(p)}
                  className={`w-full text-left px-3 py-2.5 text-sm border-b border-gray-50 last:border-0 ${isDist ? 'hover:bg-indigo-50' : 'hover:bg-emerald-50'}`}
                >
                  <span className="font-medium text-gray-800">{p.productName}</span>
                  <span className="text-[11px] text-gray-400 block">
                    {p.isCommonInventory ? 'Common inventory' : `${p.options?.length || 0} option(s)`}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className={`rounded-xl border ${isDist ? 'border-indigo-200 bg-white' : 'border-emerald-200 bg-white'} p-3 space-y-2.5`}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{picked.productName}</p>
              <p className="text-[11px] text-gray-500">
                {picked.isCommonInventory
                  ? 'Common inventory'
                  : 'Select one or more variants — panel stays open until you click Done'}
              </p>
            </div>
            <button
              type="button"
              onClick={closePicker}
              className="text-[11px] text-gray-400 hover:text-gray-600 whitespace-nowrap px-1"
            >
              Change product
            </button>
          </div>

          {!picked.isCommonInventory && picked.options?.length > 0 && (
            <>
              <div className="flex items-center justify-between gap-2">
                <span className="text-[11px] font-medium text-gray-500">Variants</span>
                {optionRows.some((r) => !r.added) && (
                  <button type="button" onClick={selectAllPending} className="text-[10px] text-gray-500 hover:text-gray-700 underline">
                    Select all remaining
                  </button>
                )}
              </div>
              <div className="space-y-1.5 max-h-40 overflow-y-auto pr-0.5">
                {optionRows.map((r) => (
                  <div
                    key={r.optionId}
                    className={`flex items-center gap-2 p-2 rounded-lg border transition-all ${
                      r.added
                        ? 'border-gray-100 bg-gray-50 opacity-80'
                        : r.selected
                          ? activeRow
                          : 'border-gray-100 bg-gray-50/50'
                    }`}
                  >
                    <button
                      type="button"
                      disabled={r.added}
                      onClick={() => toggleRow(r.optionId)}
                      className={`flex-shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center ${
                        r.added
                          ? 'border-gray-300 bg-gray-100 text-gray-400 cursor-default'
                          : r.selected
                            ? (isDist ? 'border-indigo-500 bg-indigo-500 text-white' : 'border-emerald-500 bg-emerald-500 text-white')
                            : 'border-gray-300 bg-white'
                      }`}
                    >
                      {(r.selected || r.added) && <FaCheckCircle className="text-[10px]" />}
                    </button>
                    <span className={`flex-1 text-sm truncate ${r.added ? 'text-gray-500' : 'text-gray-800 font-medium'}`}>
                      {r.label}
                    </span>
                    {r.added ? (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${chipOn}`}>Added</span>
                    ) : (
                      <input
                        type="number"
                        min="1"
                        value={r.quantity}
                        onChange={(e) => updateRowQty(r.optionId, e.target.value)}
                        onFocus={() => { if (!r.selected) toggleRow(r.optionId); }}
                        className={`w-14 py-1 px-1 text-sm text-center border border-gray-200 rounded-lg focus:outline-none focus:ring-2 ${ringClass} bg-white`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="flex items-center gap-2 pt-1 border-t border-gray-100">
            <label className="text-[11px] text-gray-500 whitespace-nowrap">Default qty</label>
            <input
              type="number"
              min="1"
              value={defaultQty}
              onChange={(e) => handleDefaultQtyChange(e.target.value)}
              className={`w-14 py-1.5 px-1 text-sm text-center border border-gray-200 rounded-lg focus:outline-none focus:ring-2 ${ringClass} bg-white`}
            />
            <button
              type="button"
              onClick={handleAdd}
              disabled={adding || !branchId || (!picked.isCommonInventory && !pendingCount)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold text-white disabled:opacity-50 ${btnClass}`}
            >
              {adding ? <FaSpinner className="animate-spin" /> : <FaPlus className="text-[10px]" />}
              {picked.isCommonInventory ? 'Add to list' : `Add selected${pendingCount ? ` (${pendingCount})` : ''}`}
            </button>
          </div>

          {!picked.isCommonInventory && (
            <button
              type="button"
              onClick={closePicker}
              className="w-full text-center text-[11px] text-gray-500 hover:text-gray-700 py-1"
            >
              Done — search another product
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ------------------------------------------------------------------
// Inventory action card — the whole update happens here, in one step
// ------------------------------------------------------------------
// Choose the option to pre-select for a product: the Brain's matched option,
// else the only option, else none (user must pick).
const initialOptionFor = (product) => {
  if (!product || product.isCommonInventory) return null;
  if (product.preselectedOptionId) return product.preselectedOptionId;
  if (product.options.length === 1) return product.options[0].optionId;
  return null;
};

const initialSelectedOptions = (product) => {
  if (!product?.preselectedOptions?.length) return [];
  return product.preselectedOptions.map((o) => o.optionId);
};

const isWarehouseBranch = (b) => !!(b?.isWarehouse || b?.masterAccount);

const buildOptionLines = (product, preselectedIds, parsedQty) => {
  if (!product?.options?.length) return [];
  const ids = preselectedIds?.length
    ? preselectedIds
    : (product.options.length === 1 ? [product.options[0].optionId] : []);
  return product.options.map((o) => ({
    optionId: o.optionId,
    label: o.label,
    enabled: ids.includes(o.optionId),
    quantity: ids.includes(o.optionId) && parsedQty != null ? String(parsedQty) : ''
  }));
};

const enrichSingleUpdateCard = (data) => {
  if (data.optionLines?.length || !data.candidates?.[0]) return data;
  const candidate = data.candidates[0];
  const preIds = (candidate.preselectedOptions || []).map((o) => o.optionId);
  const optionLines = buildOptionLines(candidate, preIds, data.parsedQuantity);
  return { ...data, optionLines };
};

const InventoryCard = ({ card, onResolved, onDraftChange }) => {
  const {
    candidates = [], branches = [], defaultBranchId, preselectedBranchId,
    branchAutoMatched, parsedQuantity, quantityMode = 'add_each', inventoryAction,
    canEdit, warehouseBranchId, warehouseBranchName
  } = card;

  const labels = getActionLabels(quantityMode, inventoryAction);
  const isRemove = quantityMode === 'remove_each';
  const isAdd = quantityMode === 'add_each';

  const [productId, setProductId] = useState(candidates.length === 1 ? candidates[0].productId : null);
  const [branchId, setBranchId] = useState(preselectedBranchId || defaultBranchId || branches[0]?._id || null);
  const product = candidates.find((c) => c.productId === productId) || null;

  const hasOptionLines = product && !product.isCommonInventory && product.options.length > 1;

  const [optionId, setOptionId] = useState(initialOptionFor(product));
  const [optionLines, setOptionLines] = useState(() =>
    card.optionLines?.length
      ? card.optionLines
      : buildOptionLines(product, initialSelectedOptions(product), parsedQuantity)
  );
  const [quantity, setQuantity] = useState(parsedQuantity != null ? String(parsedQuantity) : '');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [warehouseError, setWarehouseError] = useState(null);
  const [removeStep, setRemoveStep] = useState('qty');
  const [removeDisposition, setRemoveDisposition] = useState(null);

  useEffect(() => {
    setOptionId(initialOptionFor(product));
    if (!card._draftRevision) {
      setOptionLines(buildOptionLines(product, initialSelectedOptions(product), parsedQuantity));
    }
    setQuantity(parsedQuantity != null ? String(parsedQuantity) : '');
    setWarehouseError(null);
    setRemoveStep('qty');
    setRemoveDisposition(null);
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (card._draftRevision && card.optionLines?.length) {
      setOptionLines(card.optionLines);
    }
    if (card.preselectedBranchId) setBranchId(card.preselectedBranchId);
  }, [card._draftRevision, card.optionLines, card.preselectedBranchId]);

  useEffect(() => {
    setRemoveStep('qty');
    setRemoveDisposition(null);
  }, [branchId, quantity, optionLines, quantityMode]);

  useEffect(() => {
    if (result || !onDraftChange || !product) return;
    onDraftChange({
      intent: 'update',
      productId: product.productId,
      productName: product.productName,
      branchId,
      preselectedBranchId: branchId,
      quantityMode,
      inventoryAction: labels.action,
      parsedQuantity,
      optionLines,
      quantity,
      candidates,
      branches,
      defaultBranchId,
      warehouseBranchId,
      warehouseBranchName,
      branchAutoMatched,
      canEdit,
      _draftRevision: card._draftRevision || 0
    });
  }, [optionLines, branchId, productId, quantity, result, product, onDraftChange, card._draftRevision, labels.action]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedBranch = branches.find((b) => b._id === branchId);
  const isWarehouseTarget = isWarehouseBranch(selectedBranch);
  const needsRemoveDisposition = isRemove && !isWarehouseTarget;
  const onDispositionStep = needsRemoveDisposition && removeStep === 'disposition';

  const toggleOptionLine = (optionIdToToggle) => {
    setOptionLines((prev) => prev.map((l) =>
      l.optionId === optionIdToToggle ? { ...l, enabled: !l.enabled } : l
    ));
    setWarehouseError(null);
  };

  const updateOptionLineQty = (optionIdToUpdate, raw) => {
    setOptionLines((prev) => prev.map((l) =>
      l.optionId === optionIdToUpdate ? { ...l, quantity: raw, enabled: raw !== '' ? true : l.enabled } : l
    ));
    setWarehouseError(null);
  };

  const enabledOptionLines = optionLines.filter((l) => l.enabled && l.quantity !== '' && !isNaN(l.quantity) && +l.quantity > 0);

  const evaluatedLines = hasOptionLines
    ? enabledOptionLines.map((l) => {
        const branchStock = product.stock?.[`${branchId}_${l.optionId}`] ?? 0;
        const whStock = warehouseBranchId ? (product.stock?.[`${warehouseBranchId}_${l.optionId}`] ?? 0) : null;
        const qty = parseFloat(l.quantity);
        const newQuantity = computePreviewQuantity(branchStock, qty, quantityMode);
        const transferNeeded = isRemove ? 0 : Math.max(0, newQuantity - branchStock);
        const insufficient = isRemove
          ? qty > branchStock
          : (!isWarehouseTarget && warehouseBranchId && transferNeeded > 0 && whStock != null && transferNeeded > whStock);
        return { ...l, branchStock, whStock, newQuantity, transferNeeded, insufficient };
      })
    : [];

  const stockKey = product
    ? `${branchId}_${product.isCommonInventory ? 'common' : (optionId || 'none')}`
    : null;
  const currentStock = product && stockKey != null ? (product.stock?.[stockKey] ?? 0) : null;
  const warehouseStockKey = product && warehouseBranchId
    ? `${warehouseBranchId}_${product.isCommonInventory ? 'common' : (optionId || 'none')}`
    : null;
  const warehouseStock = warehouseStockKey != null ? (product?.stock?.[warehouseStockKey] ?? 0) : null;

  const parsedQty = quantity !== '' && !isNaN(quantity) ? parseFloat(quantity) : null;
  const previewQty = parsedQty != null ? computePreviewQuantity(currentStock, parsedQty, quantityMode) : null;
  const transferNeeded = !hasOptionLines && !isWarehouseTarget && !isRemove && previewQty != null
    ? Math.max(0, previewQty - (currentStock ?? 0))
    : 0;
  const insufficientBranch = !hasOptionLines && isRemove && parsedQty != null && parsedQty > (currentStock ?? 0);
  const insufficientWarehouse = !hasOptionLines && !isRemove && !isWarehouseTarget && transferNeeded > 0 && warehouseStock != null && transferNeeded > warehouseStock;
  const multiInsufficient = hasOptionLines && evaluatedLines.some((l) => l.insufficient);
  const stockInsufficient = multiInsufficient || insufficientWarehouse || insufficientBranch;

  const validQty = quantity !== '' && !isNaN(quantity) && parseFloat(quantity) > 0;
  const needsOption = !hasOptionLines && product && !product.isCommonInventory && product.options.length > 1 && !optionId;
  const lineCount = hasOptionLines ? evaluatedLines.length : (validQty ? 1 : 0);
  const canConfirmQty = canEdit && product && branchId && !submitting && !result && !stockInsufficient && !onDispositionStep && (
    hasOptionLines ? evaluatedLines.length > 0 : validQty && !needsOption
  );
  const canConfirmRemove = canEdit && product && branchId && !submitting && !result && onDispositionStep && removeDisposition && lineCount > 0;

  const buildPayload = () => {
    const disposition = needsRemoveDisposition ? removeDisposition : undefined;
    if (hasOptionLines) {
      const useMulti = evaluatedLines.length > 1 || isAdd || isRemove;
      if (useMulti) {
        return {
          multiProduct: true,
          selectedBranchId: branchId,
          quantityMode,
          removeDisposition: disposition,
          items: evaluatedLines.map((l) => ({
            productId: product.productId,
            productOptionId: l.optionId,
            productName: product.productName,
            optionLabel: l.label,
            quantity: parseFloat(l.quantity)
          }))
        };
      }
      const line = evaluatedLines[0];
      return {
        selectedBranchId: branchId,
        selectedProductId: product.productId,
        selectedProductOptionId: line.optionId,
        quantity: parseFloat(line.quantity),
        commonInventory: false
      };
    }
    const payload = {
      selectedBranchId: branchId,
      selectedProductId: product.productId,
      selectedProductOptionId: product.isCommonInventory ? null : optionId,
      quantity: parseFloat(quantity),
      commonInventory: product.isCommonInventory,
      selectedMetric: product.isCommonInventory ? product.metricLabel : null
    };
    if (isAdd || isRemove) payload.quantityMode = quantityMode;
    if (disposition) payload.removeDisposition = disposition;
    return payload;
  };

  const executeInventoryUpdate = async () => {
    setSubmitting(true);
    setWarehouseError(null);
    try {
      const res = await brainExecuteInventory(buildPayload());
      if (res.code === 200 && res.success) {
        setResult(res.data);
        toast.success(res.message || labels.successTitle);
        onResolved && onResolved(res.data);
      } else {
        const errMsg = res.error || res.message || 'Failed to update inventory';
        setWarehouseError(errMsg);
        toast.error(errMsg);
      }
    } catch (e) {
      const errData = e?.errorData?.data;
      const errMsg = e?.message || e?.errorData?.error || 'Failed to update inventory';
      if (errData?.shortages?.length) {
        setWarehouseError(errData.shortages.map((s) => (
          isRemove
            ? `${s.optionLabel || s.productName}: branch has ${s.branchAvailable ?? s.warehouseAvailable}, need ${s.required}`
            : `${s.optionLabel || s.productName}: warehouse has ${s.warehouseAvailable}, need ${s.required}`
        )).join(' · '));
      } else {
        setWarehouseError(errData
          ? `Only ${errData.warehouseAvailable} in warehouse. Need ${errData.requiredFromWarehouse}. Add stock to warehouse first.`
          : errMsg);
      }
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrimaryClick = async () => {
    if (needsRemoveDisposition && removeStep === 'qty') {
      if (!canConfirmQty) return;
      setRemoveStep('disposition');
      return;
    }
    if (!canConfirmRemove && !canConfirmQty) return;
    await executeInventoryUpdate();
  };

  const confirmLabel = () => {
    if (submitting) return `${labels.confirmVerb}ing…`;
    if (onDispositionStep) {
      return removeDisposition ? `Confirm & ${labels.confirmVerb.toLowerCase()} stock` : 'Choose what to do with removed stock';
    }
    if (stockInsufficient) {
      return isRemove ? 'Not enough branch stock' : 'Not enough warehouse stock';
    }
    if (hasOptionLines) {
      if (!evaluatedLines.length) return 'Select at least one option';
      if (needsRemoveDisposition) return `Continue — ${labels.confirmVerb.toLowerCase()} ${evaluatedLines.length} option(s)`;
      return `Confirm & ${labels.confirmVerb.toLowerCase()} ${evaluatedLines.length} option(s)`;
    }
    if (needsOption) return 'Select an option';
    if (needsRemoveDisposition) return `Continue — ${labels.confirmVerb.toLowerCase()} stock`;
    return `Confirm & ${labels.confirmVerb}`;
  };

  // ---------- Success state ----------
  if (result) {
    const batchResults = result.results || result.options;
    const isBatch = result.multiProduct || result.multiOption;
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
        <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-3">
          <FaCheckCircle /> {labels.successTitle}
        </div>
        <div className="bg-white rounded-xl border border-emerald-100 p-3 text-sm text-gray-700 space-y-1.5">
          <div className="flex justify-between gap-4">
            <span className="text-gray-500">Product</span>
            <span className="font-medium text-gray-900 text-right">{result.productName || batchResults?.[0]?.productName || 'Updated'}</span>
          </div>
          {isBatch && batchResults?.length > 0 ? (
            <div className="pt-1 border-t border-emerald-50 space-y-1">
              {result.branchName && (
                <p className="text-xs text-gray-500 mb-1">Branch: <b>{result.branchName}</b></p>
              )}
              {batchResults.map((o, i) => (
                <div key={i} className="flex justify-between gap-2 text-xs py-1">
                  <span className="text-gray-600">{o.optionLabel}</span>
                  <span className="font-medium">{o.previousQuantity} → {o.newQuantity}</span>
                </div>
              ))}
            </div>
          ) : (
            <>
              {result.optionLabel && (
                <div className="flex justify-between gap-4">
                  <span className="text-gray-500">Option</span>
                  <span className="font-medium text-gray-900 text-right">{result.optionLabel}</span>
                </div>
              )}
              <div className="flex justify-between gap-4">
                <span className="text-gray-500">Branch</span>
                <span className="font-medium text-gray-900 text-right">{result.branchName}</span>
              </div>
              <div className="flex justify-between gap-4 items-center pt-1">
                <span className="text-gray-500">Quantity</span>
                <span className="flex items-center gap-2 font-semibold">
                  <span className="text-gray-400 line-through">{result.previousQuantity}</span>
                  <FaArrowRight className="text-emerald-500 text-xs" />
                  <span className="text-emerald-700">{result.newQuantity}{result.isCommonInventory && result.metricLabel ? ` ${result.metricLabel}` : ''}</span>
                </span>
              </div>
            </>
          )}
          {(result.transferredFromWarehouse > 0 || result.totalTransferred > 0) && (
            <div className="flex justify-between gap-4 text-xs pt-1 border-t border-emerald-100 mt-1">
              <span className="text-gray-500">From warehouse</span>
              <span className="text-gray-700 text-right">
                {result.warehouseBranchName}
                <span className="text-emerald-600 font-medium"> (−{result.transferredFromWarehouse || result.totalTransferred} total)</span>
              </span>
            </div>
          )}
          {(result.totalReturnedToWarehouse > 0 || result.removeDisposition === 'return_to_warehouse') && (
            <div className="flex justify-between gap-4 text-xs pt-1 border-t border-emerald-100 mt-1">
              <span className="text-gray-500">Returned to warehouse</span>
              <span className="text-emerald-700 font-medium text-right">
                +{result.totalReturnedToWarehouse || 0} → {result.warehouseBranchName || warehouseBranchName || 'warehouse'}
              </span>
            </div>
          )}
          {result.totalDamaged > 0 && (
            <div className="flex justify-between gap-4 text-xs pt-1 border-t border-emerald-100 mt-1">
              <span className="text-gray-500">Marked damaged</span>
              <span className="text-amber-700 font-medium text-right">{result.totalDamaged} unit(s) written off</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ---------- Confirm state ----------
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      {candidates.length > 1 && (
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-500 mb-2">Did you mean?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {candidates.map((c) => (
              <button
                key={c.productId}
                type="button"
                onClick={() => setProductId(c.productId)}
                className={`text-left px-3 py-2 rounded-xl border transition-all ${
                  productId === c.productId
                    ? 'border-emerald-400 bg-emerald-50 ring-1 ring-emerald-300'
                    : 'border-gray-200 hover:border-emerald-300 hover:bg-gray-50'
                }`}
              >
                <span className="text-sm font-medium text-gray-800 block truncate">{c.productName}</span>
                {c.score != null && (
                  <span className="text-[10px] text-gray-400">{Math.round(c.score * 100)}% match</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {product && (
        <>
          <div className="flex items-center gap-2.5 mb-3 pb-3 border-b border-gray-100">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white">
              <FaBoxes className="text-sm" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{product.productName}</p>
              <p className="text-[11px] text-gray-400">
                {labels.verb} stock · {product.isCommonInventory ? 'Common inventory' : `${product.options.length} option${product.options.length === 1 ? '' : 's'}`}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Branch / Warehouse</label>
              <div className="relative">
                <select
                  value={branchId || ''}
                  onChange={(e) => { setBranchId(e.target.value); setWarehouseError(null); }}
                  className="w-full appearance-none pl-9 pr-8 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  {branches.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.branchName}{b.isWarehouse ? ' (Warehouse)' : b.masterAccount ? ' (Main)' : ''}
                    </option>
                  ))}
                </select>
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {selectedBranch?.isWarehouse ? <FaWarehouse className="text-xs" /> : <FaStore className="text-xs" />}
                </span>
              </div>
              {branchAutoMatched && (
                <p className="text-[11px] text-emerald-600 mt-1">Auto-matched from your text.</p>
              )}
            </div>

            {/* All product options — toggle + per-option quantity */}
            {hasOptionLines && (
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block">
                  Options — tap to add more, set qty each
                  <span className="text-gray-400 font-normal ml-1">
                    ({labels.modeHint})
                  </span>
                </label>
                <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                  {optionLines.map((l) => {
                    const branchQty = product.stock?.[`${branchId}_${l.optionId}`] ?? 0;
                    const whQty = warehouseBranchId ? (product.stock?.[`${warehouseBranchId}_${l.optionId}`] ?? 0) : null;
                    const evalLine = evaluatedLines.find((e) => e.optionId === l.optionId);
                    const short = evalLine?.insufficient;
                    const preview = evalLine?.newQuantity;
                    return (
                      <div
                        key={l.optionId}
                        className={`flex items-center gap-2 p-2.5 rounded-xl border transition-all ${
                          l.enabled
                            ? short ? 'border-red-200 bg-red-50' : 'border-emerald-300 bg-emerald-50/50'
                            : 'border-gray-100 bg-gray-50 opacity-75'
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => toggleOptionLine(l.optionId)}
                          className={`flex-shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center ${
                            l.enabled ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-gray-300 bg-white'
                          }`}
                        >
                          {l.enabled && <FaCheckCircle className="text-[10px]" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-800 truncate">{l.label}</p>
                          <p className="text-[10px] text-gray-500">
                            Branch: <b>{branchQty}</b>
                            {!isWarehouseTarget && whQty != null && (
                              <> · WH: <b className={short ? 'text-red-600' : ''}>{whQty}</b></>
                            )}
                            {preview != null && l.enabled && (
                              <> · → <b className="text-emerald-700">{preview}</b></>
                            )}
                          </p>
                        </div>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={l.quantity}
                          onChange={(e) => updateOptionLineQty(l.optionId, e.target.value)}
                          onFocus={() => { if (!l.enabled) toggleOptionLine(l.optionId); }}
                          placeholder="0"
                          className="w-14 py-1.5 px-1 text-sm text-center border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400 bg-white"
                        />
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-gray-400 mt-1.5 flex items-center gap-1">
                  <FaPlus className="text-[9px]" />
                  Enable another option (e.g. 50gm) and enter its quantity — {labels.confirmVerb.toLowerCase()} all in one go
                </p>
              </div>
            )}

            {!hasOptionLines && (
              <div>
                <label className="text-xs font-medium text-gray-500 mb-1 block flex items-center justify-between">
                  <span>{labels.qtyLabel}{product.isCommonInventory && product.metricLabel ? ` (${product.metricLabel})` : ''}</span>
                  {currentStock != null && (
                    <span className="text-[11px] text-gray-400">Current: <b className="text-gray-600">{currentStock}</b></span>
                  )}
                </label>
                <input
                  type="number"
                  min="0"
                  step={product.isCommonInventory ? '0.1' : '1'}
                  value={quantity}
                  onChange={(e) => { setQuantity(e.target.value); setWarehouseError(null); }}
                  placeholder={isRemove ? 'Units to remove' : 'Enter quantity'}
                  className="w-full px-3 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                />
                {isRemove && parsedQty != null && !insufficientBranch && (
                  <p className="text-[11px] text-amber-700 mt-1">After remove: <b>{previewQty}</b> at branch</p>
                )}
                {insufficientBranch && (
                  <p className="text-[11px] text-red-600 mt-1">Cannot remove {parsedQty} — branch only has {currentStock}</p>
                )}
              </div>
            )}

            {!isWarehouseTarget && warehouseBranchId && !isRemove && (
              <div className={`rounded-xl px-3 py-2.5 text-xs ${
                insufficientWarehouse || multiInsufficient ? 'bg-red-50 border border-red-200 text-red-700' : 'bg-amber-50 border border-amber-100 text-amber-800'
              }`}>
                {hasOptionLines ? (
                  multiInsufficient ? (
                    <p>Some selected options don&apos;t have enough warehouse stock.</p>
                  ) : isAdd ? (
                    <p><b>{evaluatedLines.length}</b> option(s) will receive stock from <b>{warehouseBranchName || 'warehouse'}</b>.</p>
                  ) : (
                    <p><b>{evaluatedLines.length}</b> option(s) will be set at <b>{selectedBranch?.branchName}</b>.</p>
                  )
                ) : (product.isCommonInventory || optionId) && (
                  <>
                    <div className="flex items-center gap-1.5 font-medium mb-0.5">
                      <FaWarehouse className="text-[10px]" />
                      {warehouseBranchName || 'Warehouse'} stock: <b>{warehouseStock ?? 0}</b>
                    </div>
                    {transferNeeded > 0 && (
                      insufficientWarehouse
                        ? <p>Need <b>{transferNeeded}</b> from warehouse but only <b>{warehouseStock}</b> available.</p>
                        : <p>Will transfer <b>{transferNeeded}</b> from warehouse.</p>
                    )}
                  </>
                )}
              </div>
            )}

            {isRemove && !isWarehouseTarget && removeStep === 'qty' && (
              <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2 border border-amber-100">
                Next step: choose whether removed stock returns to <b>{warehouseBranchName || 'warehouse'}</b> or is marked damaged.
              </p>
            )}

            {onDispositionStep && (
              <RemoveDispositionPanel
                branchName={selectedBranch?.branchName}
                warehouseBranchName={warehouseBranchName}
                lineCount={lineCount}
                removeDisposition={removeDisposition}
                onSelect={setRemoveDisposition}
                onBack={() => { setRemoveStep('qty'); setRemoveDisposition(null); }}
              />
            )}

            {isWarehouseTarget && (
              <p className="text-[11px] text-blue-600 bg-blue-50 rounded-lg px-3 py-2">
                Warehouse {isRemove ? 'remove' : 'update'} — stock is adjusted directly here.
              </p>
            )}

            {warehouseError && (
              <p className="text-[11px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{warehouseError}</p>
            )}

            {!canEdit && (
              <p className="text-[11px] text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                You don&apos;t have permission to edit inventory.
              </p>
            )}

            <button
              type="button"
              onClick={handlePrimaryClick}
              disabled={!(canConfirmQty || canConfirmRemove)}
              className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                (canConfirmQty || canConfirmRemove)
                  ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:shadow-lg hover:shadow-emerald-500/25'
                  : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {submitting ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
              {confirmLabel()}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ------------------------------------------------------------------
// Distribution card — warehouse → branch via approval workflow
// ------------------------------------------------------------------
const DistributionCard = ({ card, onResolved, onDraftChange, onClear }) => {
  const {
    productGroups: initialProductGroups = [],
    items: initialItems = [],
    targetBranches = [],
    targetBranchId: preselectedBranchId,
    branchAutoMatched,
    warehouseBranchId,
    warehouseBranchName,
    canDistribute
  } = card;

  const [productGroups, setProductGroups] = useState(initialProductGroups);
  const [commonItems, setCommonItems] = useState(() => (initialItems || []).filter((i) => i.commonInventory));
  const [targetBranchId, setTargetBranchId] = useState(
    preselectedBranchId || targetBranches[0]?._id || ''
  );
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (card._draftRevision && card.productGroups) {
      setProductGroups(card.productGroups);
    } else if (initialProductGroups.length) {
      setProductGroups(initialProductGroups);
    }
    if (card._draftRevision && card.items) {
      setCommonItems(card.items.filter((i) => i.commonInventory));
    }
    if (preselectedBranchId) setTargetBranchId(preselectedBranchId);
  }, [initialProductGroups, preselectedBranchId, card._draftRevision, card.productGroups, card.items]);

  const groupItems = buildDistributionItemsFromGroups(productGroups);
  const items = mergeLineItems(groupItems, commonItems.filter((i) => i.quantity !== '' && +i.quantity > 0));

  useEffect(() => {
    onDraftChange?.({ ...card, productGroups, items, targetBranchId });
  }, [productGroups, commonItems, items, targetBranchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const targetBranch = targetBranches.find((b) => b._id === targetBranchId);

  const toggleGroupOption = (productId, optionId) => {
    setProductGroups((prev) => prev.map((g) => {
      if (g.productId !== productId) return g;
      return {
        ...g,
        optionLines: g.optionLines.map((l) => {
          if (l.optionId !== optionId) return l;
          const enabled = !l.enabled;
          return {
            ...l,
            enabled,
            quantity: enabled && !l.quantity ? '10' : l.quantity
          };
        })
      };
    }));
    setError(null);
  };

  const updateGroupOptionQty = (productId, optionId, raw) => {
    setProductGroups((prev) => prev.map((g) => {
      if (g.productId !== productId) return g;
      return {
        ...g,
        optionLines: g.optionLines.map((l) => {
          if (l.optionId !== optionId) return l;
          const numQty = raw === '' ? 0 : parseFloat(raw);
          const wh = l.warehouseAvailable ?? 0;
          return {
            ...l,
            quantity: raw,
            enabled: raw !== '' ? true : l.enabled,
            insufficient: raw !== '' && !isNaN(numQty) && numQty > 0 && wh < numQty
          };
        })
      };
    }));
    setError(null);
  };

  const updateCommonQty = (key, raw) => {
    const qty = raw === '' ? '' : parseFloat(raw);
    setCommonItems((prev) => prev.map((i) => {
      if (i.key !== key) return i;
      const quantity = qty === '' || isNaN(qty) ? '' : qty;
      const numQty = quantity === '' ? 0 : quantity;
      return {
        ...i,
        quantity,
        insufficient: (i.warehouseAvailable ?? 0) < numQty
      };
    }));
    setError(null);
  };

  const removeCommonItem = (key) => {
    setCommonItems((prev) => prev.filter((i) => i.key !== key));
    setError(null);
  };

  const handleSearchAdd = (newLines, meta) => {
    const common = newLines.filter((l) => l.commonInventory);
    const variant = newLines.filter((l) => !l.commonInventory);
    if (variant.length) {
      setProductGroups((prev) => (
        meta?.product
          ? mergeSearchProductIntoGroups(prev, meta.product, variant)
          : mergeSearchLinesIntoGroups(prev, variant)
      ));
    }
    if (common.length) {
      setCommonItems((prev) => mergeLineItems(prev, common));
    }
    setError(null);
  };

  const hasInsufficient = items.some((i) => i.insufficient || (i.warehouseAvailable ?? 0) < +(i.quantity || 0));
  const validItems = items.filter((i) => i.quantity !== '' && !isNaN(i.quantity) && +i.quantity > 0);
  const defaultAddQty = validItems.length
    ? Math.round(validItems.reduce((s, i) => s + (+i.quantity || 0), 0) / validItems.length)
    : 10;
  const canConfirm = canDistribute && targetBranchId && validItems.length > 0 && !hasInsufficient && !submitting && !result;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = {
        targetBranchId,
        items: validItems.map((i) => ({
          productId: i.productId,
          productOptionId: i.productOptionId,
          productName: i.productName,
          optionLabel: i.optionLabel,
          quantity: +i.quantity,
          commonInventory: i.commonInventory,
          selectedMetric: i.selectedMetric,
          metricId: i.metricId
        })),
        notes: 'Created via Zuntly Brain'
      };
      const res = await brainExecuteDistribution(payload);
      if (res.code === 200 && res.success) {
        setResult(res.data);
        toast.success(res.message || 'Distribution request created');
        onResolved?.(res.data);
        onClear?.();
      } else {
        const errMsg = res.error || res.message || 'Failed to create distribution request';
        setError(errMsg);
        toast.error(errMsg);
      }
    } catch (e) {
      const errMsg = e?.message || e?.errorData?.error || 'Failed to create distribution request';
      setError(errMsg);
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="rounded-2xl border border-indigo-200 bg-indigo-50/60 p-4">
        <div className="flex items-center gap-2 text-indigo-700 font-semibold mb-3">
          <FaCheckCircle /> Distribution request created
        </div>
        <div className="bg-white rounded-xl border border-indigo-100 p-3 text-sm text-gray-700 space-y-1.5">
          <div className="flex justify-between gap-4">
            <span className="text-gray-500">Request ID</span>
            <span className="font-mono font-medium text-indigo-700">{result.requestId}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-gray-500">To branch</span>
            <span className="font-medium text-gray-900">{result.targetBranchName}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-gray-500">From warehouse</span>
            <span className="font-medium text-gray-900">{result.warehouseBranchName}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-gray-500">Items</span>
            <span className="font-medium text-gray-900">{result.itemCount} line(s)</span>
          </div>
        </div>
        <p className="text-[11px] text-gray-400 mt-2">
          Status: pending branch review — the branch will verify quantities, then warehouse gives final approval in Inventory Approvals.
        </p>
      </div>
    );
  }

  if (!items.length && !productGroups.length) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-500 text-center">
        No products in this distribution yet. Type another product to add, e.g. &quot;Chocolate Muffin 500gm 5&quot;.
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-indigo-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2.5 mb-3 pb-3 border-b border-gray-100">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center text-white">
          <FaTruck className="text-sm" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900">Distribute stock</p>
          <p className="text-[11px] text-gray-400">
            Warehouse → branch · {validItems.length} line(s) · approval workflow
          </p>
        </div>
        {onClear && (
          <button type="button" onClick={onClear} className="text-xs text-gray-400 hover:text-red-500 px-2 py-1">
            Clear
          </button>
        )}
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Target branch</label>
          <div className="relative">
            <select
              value={targetBranchId || ''}
              onChange={(e) => { setTargetBranchId(e.target.value); setError(null); }}
              className="w-full appearance-none pl-9 pr-8 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            >
              {targetBranches.map((b) => (
                <option key={b._id} value={b._id}>{b.branchName}</option>
              ))}
            </select>
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <FaStore className="text-xs" />
            </span>
          </div>
          {branchAutoMatched && (
            <p className="text-[11px] text-indigo-600 mt-1">Auto-matched from your text — change if needed.</p>
          )}
        </div>

        {warehouseBranchName && (
          <p className="text-[11px] text-gray-500 flex items-center gap-1.5">
            <FaWarehouse className="text-gray-400" />
            Sending from <b className="text-gray-700">{warehouseBranchName}</b>
          </p>
        )}

        <BrainProductSearchAdd
          branchId={targetBranchId}
          quantityMode="add_each"
          warehouseBranchId={warehouseBranchId}
          mode="distribute"
          onAdd={handleSearchAdd}
          defaultQuantity={defaultAddQty}
        />

        <div>
          <label className="text-xs font-medium text-gray-500 mb-2 block">
            Products to distribute — select options and qty each
          </label>
          <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
            {productGroups.map((group) => (
              <div key={group.productId} className="rounded-xl border border-indigo-100 bg-indigo-50/30 p-2.5">
                <p className="text-sm font-semibold text-gray-800 mb-2 truncate">{group.productName}</p>
                <div className="space-y-2">
                  {(group.optionLines || []).map((l) => (
                    <div
                      key={l.optionId}
                      className={`flex items-center gap-2 p-2 rounded-lg border transition-all ${
                        l.enabled
                          ? l.insufficient ? 'border-red-200 bg-red-50' : 'border-indigo-300 bg-white'
                          : 'border-gray-100 bg-gray-50/80 opacity-80'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => toggleGroupOption(group.productId, l.optionId)}
                        className={`flex-shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center ${
                          l.enabled ? 'border-indigo-500 bg-indigo-500 text-white' : 'border-gray-300 bg-white'
                        }`}
                      >
                        {l.enabled && <FaCheckCircle className="text-[10px]" />}
                      </button>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-800 truncate">{l.label}</p>
                        <p className="text-[10px] text-gray-500">
                          WH: <b className={l.insufficient ? 'text-red-600' : 'text-gray-700'}>{l.warehouseAvailable ?? 0}</b>
                        </p>
                      </div>
                      <input
                        type="number"
                        min="1"
                        value={l.quantity}
                        onChange={(e) => updateGroupOptionQty(group.productId, l.optionId, e.target.value)}
                        onFocus={() => { if (!l.enabled) toggleGroupOption(group.productId, l.optionId); }}
                        placeholder="0"
                        className="w-14 py-1.5 px-1 text-sm text-center border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}

            {commonItems.map((item) => (
              <div
                key={item.key}
                className={`flex items-center gap-2 p-2.5 rounded-xl border ${
                  item.insufficient ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-gray-50'
                }`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{item.productName}</p>
                  <p className="text-[11px] text-gray-500">
                    {item.optionLabel}
                    {item.commonInventory && item.selectedMetric ? ` · ${item.selectedMetric}` : ''}
                    {' · '}WH: <b className={item.insufficient ? 'text-red-600' : 'text-gray-700'}>{item.warehouseAvailable ?? 0}</b>
                  </p>
                </div>
                <input
                  type="number"
                  min="1"
                  value={item.quantity}
                  onChange={(e) => updateCommonQty(item.key, e.target.value)}
                  className="w-16 py-1.5 px-2 text-sm text-center border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
                <button
                  type="button"
                  onClick={() => removeCommonItem(item.key)}
                  className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50"
                  title="Remove"
                >
                  <FaTimes className="text-xs" />
                </button>
              </div>
            ))}
          </div>
        </div>

        <p className="text-[11px] text-gray-400 flex items-center gap-1">
          <FaPlus className="text-[10px]" />
          Enable more options above, or type in chat e.g. + 500gm 10
        </p>

        {error && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
        )}

        {!canDistribute && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
            Distribution requests can only be created from a warehouse account.
          </p>
        )}

        <button
          type="button"
          onClick={handleConfirm}
          disabled={!canConfirm}
          className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            canConfirm
              ? 'bg-gradient-to-r from-indigo-500 to-violet-600 text-white hover:shadow-lg hover:shadow-indigo-500/25'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          {submitting ? <FaSpinner className="animate-spin" /> : <FaTruck />}
          {submitting ? 'Creating request…' : hasInsufficient ? 'Not enough warehouse stock' : !targetBranchId ? 'Select target branch' : `Create distribution to ${targetBranch?.branchName || 'branch'}`}
        </button>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// Multi-product update card — batch stock update with add-more support
// ------------------------------------------------------------------
const MultiUpdateCard = ({ card, onResolved, onDraftChange, onClear, onRecalculate, chatHint }) => {
  const {
    items: initialItems = [],
    branches = [],
    branchId: preselectedBranchId,
    branchAutoMatched,
    warehouseBranchId,
    warehouseBranchName,
    quantityMode = 'add_each',
    inventoryAction,
    canEdit
  } = card;

  const labels = getActionLabels(quantityMode, inventoryAction);
  const isRemove = quantityMode === 'remove_each';
  const isAdd = quantityMode === 'add_each';

  const [items, setItems] = useState(initialItems);
  const [branchId, setBranchId] = useState(preselectedBranchId || branches[0]?._id || '');
  const [submitting, setSubmitting] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [removeStep, setRemoveStep] = useState('qty');
  const [removeDisposition, setRemoveDisposition] = useState(null);

  useEffect(() => {
    setItems(initialItems);
    if (preselectedBranchId) setBranchId(preselectedBranchId);
    setRemoveStep('qty');
    setRemoveDisposition(null);
  }, [initialItems, preselectedBranchId, quantityMode]);

  useEffect(() => {
    onDraftChange?.({ ...card, items, branchId, quantityMode, inventoryAction: labels.action });
  }, [items, branchId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedBranch = branches.find((b) => b._id === branchId);
  const isWarehouseTarget = isWarehouseBranch(selectedBranch);
  const needsRemoveDisposition = isRemove && !isWarehouseTarget;
  const onDispositionStep = needsRemoveDisposition && removeStep === 'disposition';

  const updateQty = (key, raw) => {
    const qty = raw === '' ? '' : parseFloat(raw);
    setItems((prev) => prev.map((i) => {
      if (i.key !== key) return i;
      const quantity = qty === '' || isNaN(qty) ? '' : qty;
      const numQty = quantity === '' ? 0 : quantity;
      const branchStock = i.branchStock ?? 0;
      const newQuantity = computePreviewQuantity(branchStock, numQty, quantityMode);
      const transferNeeded = isRemove ? 0 : Math.max(0, newQuantity - branchStock);
      const insufficient = isRemove
        ? numQty > branchStock
        : (!isWarehouseTarget && warehouseBranchId && transferNeeded > 0
          && (i.warehouseAvailable ?? 0) < transferNeeded);
      return { ...i, quantity, newQuantity, transferNeeded, insufficient };
    }));
    setError(null);
    setRemoveStep('qty');
    setRemoveDisposition(null);
  };

  const removeItem = (key) => {
    setItems((prev) => prev.filter((i) => i.key !== key));
    setError(null);
  };

  const handleSearchAdd = (newLines) => {
    setItems((prev) => mergeLineItems(prev, newLines));
    setError(null);
  };

  const handleBranchChange = async (newBranchId) => {
    setBranchId(newBranchId);
    setError(null);
    setRemoveStep('qty');
    setRemoveDisposition(null);
    if (!items.length || !onRecalculate) return;
    setRecalculating(true);
    try {
      const data = await onRecalculate(items, newBranchId, quantityMode);
      if (data?.items) setItems(data.items);
    } catch (e) {
      toast.error('Could not refresh stock for branch');
    } finally {
      setRecalculating(false);
    }
  };

  const hasInsufficient = items.some((i) => i.insufficient);
  const validItems = items.filter((i) => i.quantity !== '' && !isNaN(i.quantity) && +i.quantity > 0);
  const defaultAddQty = validItems.length
    ? Math.round(validItems.reduce((s, i) => s + (+i.quantity || 0), 0) / validItems.length)
    : 10;
  const canConfirmQty = canEdit && branchId && validItems.length > 0 && !hasInsufficient && !submitting && !result && !recalculating && !onDispositionStep;
  const canConfirmRemove = canEdit && branchId && validItems.length > 0 && !submitting && !result && onDispositionStep && removeDisposition;

  const executeMultiUpdate = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await brainExecuteInventory({
        multiProduct: true,
        selectedBranchId: branchId,
        quantityMode,
        removeDisposition: needsRemoveDisposition ? removeDisposition : undefined,
        items: validItems.map((i) => ({
          productId: i.productId,
          productOptionId: i.productOptionId,
          productName: i.productName,
          optionLabel: i.optionLabel,
          quantity: +i.quantity,
          commonInventory: i.commonInventory,
          selectedMetric: i.selectedMetric,
          metricId: i.metricId
        }))
      });
      if (res.code === 200 && res.success) {
        setResult(res.data);
        toast.success(res.message || labels.successTitle);
        onResolved?.(res.data);
        onClear?.();
      } else {
        const errMsg = res.error || res.message || 'Failed to update inventory';
        setError(errMsg);
        toast.error(errMsg);
      }
    } catch (e) {
      const errData = e?.errorData?.data;
      const errMsg = e?.message || e?.errorData?.error || 'Failed to update inventory';
      if (errData?.shortages?.length) {
        setError(errData.shortages.map((s) => (
          isRemove
            ? `${s.productName || s.optionLabel}: branch has ${s.branchAvailable ?? s.warehouseAvailable}, need ${s.required}`
            : `${s.productName || s.optionLabel}: warehouse has ${s.warehouseAvailable}, need ${s.required}`
        )).join(' · '));
      } else {
        setError(errMsg);
      }
      toast.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrimaryClick = async () => {
    if (needsRemoveDisposition && removeStep === 'qty') {
      if (!canConfirmQty) return;
      setRemoveStep('disposition');
      return;
    }
    if (!canConfirmRemove && !canConfirmQty) return;
    await executeMultiUpdate();
  };

  const confirmLabel = () => {
    if (submitting) return `${labels.confirmVerb}ing…`;
    if (onDispositionStep) {
      return removeDisposition ? `Confirm & ${labels.confirmVerb.toLowerCase()} ${validItems.length} line(s)` : 'Choose what to do with removed stock';
    }
    if (hasInsufficient) return isRemove ? 'Not enough branch stock' : 'Not enough warehouse stock';
    if (needsRemoveDisposition) return `Continue — ${labels.confirmVerb.toLowerCase()} ${validItems.length} line(s)`;
    return `Confirm & ${labels.confirmVerb.toLowerCase()} ${validItems.length} line(s)`;
  };

  if (result) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
        <div className="flex items-center gap-2 text-emerald-700 font-semibold mb-3">
          <FaCheckCircle /> {labels.successTitle}
        </div>
        <div className="bg-white rounded-xl border border-emerald-100 p-3 text-sm space-y-2 max-h-48 overflow-y-auto">
          {(result.results || []).map((r, i) => (
            <div key={i} className="flex justify-between gap-2 text-xs">
              <span className="text-gray-600 truncate">{r.productName} · {r.optionLabel}</span>
              <span className="font-medium text-emerald-700 whitespace-nowrap">{r.previousQuantity} → {r.newQuantity}</span>
            </div>
          ))}
        </div>
        {result.totalTransferred > 0 && (
          <p className="text-[11px] text-gray-500 mt-2">
            {result.totalTransferred} unit(s) transferred from {result.warehouseBranchName || 'warehouse'}.
          </p>
        )}
        {result.totalReturnedToWarehouse > 0 && (
          <p className="text-[11px] text-gray-500 mt-2">
            {result.totalReturnedToWarehouse} unit(s) returned to {result.warehouseBranchName || 'warehouse'}.
          </p>
        )}
        {result.totalDamaged > 0 && (
          <p className="text-[11px] text-amber-700 mt-2">
            {result.totalDamaged} unit(s) marked as damaged.
          </p>
        )}
      </div>
    );
  }

  if (!items.length) {
    return (
      <div className="rounded-2xl border border-emerald-200 bg-white p-4 shadow-sm space-y-3">
        <p className="text-sm font-semibold text-gray-900">Multi-product {labels.verb.toLowerCase()}</p>
        <BrainProductSearchAdd
          branchId={branchId}
          quantityMode={quantityMode}
          warehouseBranchId={warehouseBranchId}
          mode="update"
          onAdd={handleSearchAdd}
          defaultQuantity={10}
        />
        <p className="text-[11px] text-gray-400 text-center">
          Or type a command in chat, e.g. {chatHint || FALLBACK_CATALOG_HINTS.multiChatHint}
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-emerald-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2.5 mb-3 pb-3 border-b border-gray-100">
        <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white">
          <FaBoxes className="text-sm" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900">Multi-product {labels.verb.toLowerCase()}</p>
          <p className="text-[11px] text-gray-400">
            {items.length} line(s) · {labels.modeHint}
          </p>
        </div>
        {onClear && (
          <button type="button" onClick={onClear} className="text-xs text-gray-400 hover:text-red-500 px-2 py-1">Clear</button>
        )}
      </div>

      <div className="space-y-3">
        <div>
          <label className="text-xs font-medium text-gray-500 mb-1 block">Branch / Warehouse</label>
          <div className="relative">
            <select
              value={branchId || ''}
              onChange={(e) => handleBranchChange(e.target.value)}
              disabled={recalculating}
              className="w-full appearance-none pl-9 pr-8 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {branches.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.branchName}{b.isWarehouse ? ' (Warehouse)' : b.masterAccount ? ' (Main)' : ''}
                </option>
              ))}
            </select>
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              {recalculating ? <FaSpinner className="animate-spin text-xs" /> : (selectedBranch?.isWarehouse ? <FaWarehouse className="text-xs" /> : <FaStore className="text-xs" />)}
            </span>
          </div>
          {branchAutoMatched && (
            <p className="text-[11px] text-emerald-600 mt-1">Auto-matched from your text.</p>
          )}
        </div>

        {!isWarehouseTarget && warehouseBranchName && isAdd && (
          <p className="text-[11px] text-gray-500 flex items-center gap-1.5">
            <FaWarehouse className="text-gray-400" />
            Increases funded from <b className="text-gray-700">{warehouseBranchName}</b>
          </p>
        )}

        {isRemove && !isWarehouseTarget && removeStep === 'qty' && (
          <p className="text-[11px] text-amber-700 bg-amber-50 rounded-lg px-3 py-2 border border-amber-100">
            Next step: choose whether removed stock returns to <b>{warehouseBranchName || 'warehouse'}</b> or is marked damaged.
          </p>
        )}

        {onDispositionStep && (
          <RemoveDispositionPanel
            branchName={selectedBranch?.branchName}
            warehouseBranchName={warehouseBranchName}
            lineCount={validItems.length}
            removeDisposition={removeDisposition}
            onSelect={setRemoveDisposition}
            onBack={() => { setRemoveStep('qty'); setRemoveDisposition(null); }}
          />
        )}

        <BrainProductSearchAdd
          branchId={branchId}
          quantityMode={quantityMode}
          warehouseBranchId={warehouseBranchId}
          mode="update"
          onAdd={handleSearchAdd}
          defaultQuantity={defaultAddQty}
        />

        <div>
          <label className="text-xs font-medium text-gray-500 mb-2 block">Products in this {labels.verb.toLowerCase()}</label>
          <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
            {items.map((item) => (
              <div
                key={item.key}
                className={`p-2.5 rounded-xl border ${item.insufficient ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-gray-50'}`}
              >
                <div className="flex items-center gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{item.productName}</p>
                    <p className="text-[11px] text-gray-500">
                      {item.optionLabel}
                      {' · '}Branch: <b>{item.branchStock ?? 0}</b>
                      {!isWarehouseTarget && item.warehouseAvailable != null && (
                        <> · WH: <b className={item.insufficient ? 'text-red-600' : ''}>{item.warehouseAvailable}</b></>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateQty(item.key, e.target.value)}
                      className="w-14 py-1.5 px-1 text-sm text-center border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    />
                    <p className="text-[10px] text-gray-400 mt-0.5">
                      → {item.newQuantity ?? '?'}
                    </p>
                  </div>
                  <button type="button" onClick={() => removeItem(item.key)} className="p-1.5 text-gray-400 hover:text-red-500 rounded-lg">
                    <FaTimes className="text-xs" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-[11px] text-gray-400 flex items-center gap-1">
          <FaPlus className="text-[10px]" />
          Type more products below to add to this {labels.verb.toLowerCase()}
        </p>

        {error && (
          <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
        )}

        <button
          type="button"
          onClick={handlePrimaryClick}
          disabled={!(canConfirmQty || canConfirmRemove)}
          className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            (canConfirmQty || canConfirmRemove)
              ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white hover:shadow-lg hover:shadow-emerald-500/25'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        >
          {submitting ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />}
          {confirmLabel()}
        </button>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------
// Stock query card — pick a branch, then see live availability
// ------------------------------------------------------------------
const QueryCard = ({ card }) => {
  const { candidates = [], branches = [], defaultBranchId, preselectedBranchId, branchAutoMatched } = card;

  const [productId, setProductId] = useState(candidates.length === 1 ? candidates[0].productId : null);
  const [branchId, setBranchId] = useState(preselectedBranchId || defaultBranchId || branches[0]?._id || null);
  const [checked, setChecked] = useState(false);

  const product = candidates.find((c) => c.productId === productId) || null;
  const selectedBranch = branches.find((b) => b._id === branchId);

  const lookup = (optKey) => product?.stock?.[`${branchId}_${optKey}`] ?? 0;

  const rows = product && !product.isCommonInventory
    ? product.options.map((o) => ({ label: o.label, qty: lookup(o.optionId), highlight: o.optionId === product.preselectedOptionId }))
    : [];
  const total = rows.reduce((s, r) => s + (Number(r.qty) || 0), 0);
  const commonQty = product?.isCommonInventory ? lookup('common') : null;

  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      {candidates.length > 1 && (
        <div className="mb-3">
          <p className="text-xs font-medium text-gray-500 mb-2">Did you mean?</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {candidates.map((c) => (
              <button
                key={c.productId}
                onClick={() => { setProductId(c.productId); setChecked(false); }}
                className={`text-left px-3 py-2 rounded-xl border transition-all ${
                  productId === c.productId
                    ? 'border-emerald-400 bg-emerald-50 ring-1 ring-emerald-300'
                    : 'border-gray-200 hover:border-emerald-300 hover:bg-gray-50'
                }`}
              >
                <span className="text-sm font-medium text-gray-800 block truncate">{c.productName}</span>
                {c.score != null && <span className="text-[10px] text-gray-400">{Math.round(c.score * 100)}% match</span>}
              </button>
            ))}
          </div>
        </div>
      )}

      {product && (
        <>
          <div className="flex items-center gap-2.5 mb-3 pb-3 border-b border-gray-100">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white">
              <FaSearch className="text-sm" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{product.productName}</p>
              <p className="text-[11px] text-gray-400">Check available stock</p>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-xs font-medium text-gray-500 mb-1 block">Branch / Warehouse</label>
              <div className="relative">
                <select
                  value={branchId || ''}
                  onChange={(e) => setBranchId(e.target.value)}
                  className="w-full appearance-none pl-9 pr-8 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  {branches.map((b) => (
                    <option key={b._id} value={b._id}>
                      {b.branchName}{b.isWarehouse ? ' (Warehouse)' : b.masterAccount ? ' (Main)' : ''}
                    </option>
                  ))}
                </select>
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                  {selectedBranch?.isWarehouse ? <FaWarehouse className="text-xs" /> : <FaStore className="text-xs" />}
                </span>
              </div>
              {branchAutoMatched && (
                <p className="text-[11px] text-emerald-600 mt-1">Auto-matched from your text — change it if needed.</p>
              )}
            </div>

            {!checked ? (
              <button
                onClick={() => setChecked(true)}
                disabled={!branchId}
                className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  branchId ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:shadow-lg hover:shadow-blue-500/25' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                <FaSearch /> Check stock
              </button>
            ) : (
              <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500">{selectedBranch?.branchName}</span>
                  {!product.isCommonInventory && (
                    <span className="text-xs text-gray-500">Total: <b className="text-gray-800">{total}</b></span>
                  )}
                </div>

                {product.isCommonInventory ? (
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-2xl font-bold text-gray-900">{commonQty}</span>
                    <span className="text-sm text-gray-500">{product.metricLabel}</span>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    {rows.map((r, i) => (
                      <div
                        key={i}
                        className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-sm ${
                          r.highlight ? 'bg-emerald-100 text-emerald-800' : 'bg-white text-gray-700'
                        }`}
                      >
                        <span className="truncate">{r.label}</span>
                        <span className={`font-semibold ${Number(r.qty) <= 0 ? 'text-red-500' : ''}`}>{r.qty}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ------------------------------------------------------------------
// Expiry helpers
// ------------------------------------------------------------------
const formatExpiryDateInput = (value) => {
  const digits = String(value || '').replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

const isValidExpiryDateFormat = (value) => {
  if (!value) return true;
  const match = String(value).trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return false;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || year < 1900) return false;
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
};

const getExpiryStatus = (expiryDate) => {
  if (!expiryDate) return { key: 'none', label: 'No expiry', daysLeft: null };
  const [day, month, year] = expiryDate.split('/').map(Number);
  if (!day || !month || !year) return { key: 'none', label: 'No expiry', daysLeft: null };
  const exp = new Date(year, month - 1, day);
  const today = new Date();
  exp.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((exp - today) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return { key: 'expired', label: 'Expired', daysLeft: diffDays };
  if (diffDays <= 5) return { key: 'critical', label: `${diffDays}d left`, daysLeft: diffDays };
  if (diffDays <= 30) return { key: 'warning', label: `${diffDays}d left`, daysLeft: diffDays };
  return { key: 'good', label: `${diffDays}d left`, daysLeft: diffDays };
};

const expiryStatusStyles = {
  expired: 'bg-red-100 text-red-700',
  critical: 'bg-red-50 text-red-600',
  warning: 'bg-amber-50 text-amber-700',
  good: 'bg-emerald-50 text-emerald-700',
  none: 'bg-gray-100 text-gray-500'
};

const buildExpiryOptionLines = (product, preselectedIds = [], parsedDate = '') => {
  if (!product?.options?.length) return [];
  const ids = preselectedIds.length ? preselectedIds : product.options.map((o) => o.optionId);
  return product.options.map((o) => ({
    optionId: o.optionId,
    label: o.label,
    enabled: ids.includes(o.optionId),
    expiryDate: ids.includes(o.optionId) && parsedDate ? parsedDate : (o.expiryDate || ''),
    currentExpiryDate: o.expiryDate || ''
  }));
};

const ExpiryQueryCard = ({ card }) => {
  const { items = [], queryType, daysWindow, candidates = [] } = card;

  if (candidates.length === 1 && queryType === 'show_product') {
    const c = candidates[0];
    return (
      <div className="rounded-2xl border border-violet-200 bg-violet-50/40 p-4">
        <p className="text-sm font-semibold text-gray-900 mb-3">{c.productName}</p>
        <div className="space-y-2">
          {(c.options || []).map((o) => {
            const st = getExpiryStatus(o.expiryDate);
            return (
              <div key={o.optionId} className="flex justify-between gap-2 p-2.5 rounded-xl bg-white border border-violet-100 text-sm">
                <span className="text-gray-700">{o.label}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${expiryStatusStyles[st.key]}`}>
                  {o.expiryDate || 'No expiry'}{o.expiryDate ? ` · ${st.label}` : ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const title = queryType === 'already_expired'
    ? 'Already expired'
    : `Expiring within ${daysWindow ?? 30} days`;

  return (
    <div className="rounded-2xl border border-violet-200 bg-violet-50/40 p-4">
      <div className="flex items-center gap-2 mb-3">
        <FaCalendarAlt className="text-violet-600" />
        <p className="text-sm font-semibold text-gray-900">{title}</p>
        <span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-full">{items.length} option(s)</span>
      </div>
      {!items.length ? (
        <p className="text-sm text-gray-500">No matching product options with expiry dates in this range.</p>
      ) : (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {items.map((item) => {
            const st = getExpiryStatus(item.expiryDate);
            return (
              <div key={item.key} className="flex items-center gap-2 p-2.5 rounded-xl bg-white border border-violet-100">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{item.productName}</p>
                  <p className="text-[11px] text-gray-500">{item.optionLabel}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-mono text-gray-800">{item.expiryDate}</p>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${expiryStatusStyles[st.key]}`}>
                    {st.key === 'expired' ? <FaExclamationTriangle className="inline mr-0.5" /> : null}
                    {st.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ExpirySetCard = ({ card, onResolved }) => {
  const { candidates = [], parsedExpiryDate = '', clearExpiry, optionLines: initialLines, canEdit } = card;
  const [productId, setProductId] = useState(candidates.length === 1 ? candidates[0].productId : null);
  const product = candidates.find((c) => c.productId === productId) || null;
  const [optionLines, setOptionLines] = useState(() =>
    initialLines?.length
      ? initialLines.map((l) => ({ ...l, enabled: true }))
      : buildExpiryOptionLines(product, product?.preselectedOptions?.map((o) => o.optionId) || [], parsedExpiryDate)
  );
  const [bulkDate, setBulkDate] = useState(parsedExpiryDate || '');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!initialLines?.length && product) {
      setOptionLines(buildExpiryOptionLines(product, product.preselectedOptions?.map((o) => o.optionId) || [], parsedExpiryDate));
    }
    setBulkDate(parsedExpiryDate || '');
  }, [productId]); // eslint-disable-line react-hooks/exhaustive-deps

  const enabledLines = optionLines.filter((l) => l.enabled);
  const canConfirm = canEdit && product && enabledLines.length > 0 && !submitting && !result
    && (clearExpiry || enabledLines.every((l) => !l.expiryDate || isValidExpiryDateFormat(l.expiryDate)));

  const applyBulkDate = (raw) => {
    const formatted = formatExpiryDateInput(raw);
    setBulkDate(formatted);
    setOptionLines((prev) => prev.map((l) => (l.enabled ? { ...l, expiryDate: formatted } : l)));
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      const res = await brainExecuteExpiry({
        items: enabledLines.map((l) => ({
          productId: product.productId,
          productOptionId: l.optionId,
          productName: product.productName,
          optionLabel: l.label,
          expiryDate: clearExpiry ? '' : l.expiryDate,
          currentExpiryDate: l.currentExpiryDate
        }))
      });
      if (res.code === 200 && res.success) {
        setResult(res.data);
        toast.success(res.message);
        onResolved?.(res.data);
      } else {
        toast.error(res.error || res.message || 'Failed to save expiry');
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to save expiry');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="rounded-2xl border border-violet-200 bg-violet-50/60 p-4">
        <div className="flex items-center gap-2 text-violet-700 font-semibold mb-3">
          <FaCheckCircle /> Expiry updated
        </div>
        <div className="bg-white rounded-xl border border-violet-100 p-3 text-sm space-y-1.5">
          {(result.results || []).map((r, i) => (
            <div key={i} className="flex justify-between gap-2 text-xs">
              <span className="text-gray-600">{r.optionLabel}</span>
              <span className="font-medium">{r.previousExpiryDate || '—'} → {r.expiryDate || 'Cleared'}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-violet-200 bg-white p-4 shadow-sm">
      {candidates.length > 1 && (
        <div className="mb-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          {candidates.map((c) => (
            <button
              key={c.productId}
              type="button"
              onClick={() => setProductId(c.productId)}
              className={`text-left px-3 py-2 rounded-xl border ${productId === c.productId ? 'border-violet-400 bg-violet-50' : 'border-gray-200'}`}
            >
              <span className="text-sm font-medium">{c.productName}</span>
            </button>
          ))}
        </div>
      )}
      {product && (
        <>
          <div className="flex items-center gap-2 mb-3 pb-3 border-b border-gray-100">
            <FaCalendarAlt className="text-violet-600" />
            <div>
              <p className="text-sm font-semibold text-gray-900">{product.productName}</p>
              <p className="text-[11px] text-gray-400">{clearExpiry ? 'Clear expiry dates' : 'Set expiry dates (DD/MM/YYYY)'}</p>
            </div>
          </div>
          {!clearExpiry && (
            <div className="mb-3">
              <label className="text-xs text-gray-500 mb-1 block">Apply date to all selected options</label>
              <input
                type="text"
                value={bulkDate}
                onChange={(e) => applyBulkDate(e.target.value)}
                placeholder="DD/MM/YYYY"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:ring-2 focus:ring-violet-400"
              />
            </div>
          )}
          <div className="space-y-2 max-h-56 overflow-y-auto">
            {optionLines.map((l) => (
              <div key={l.optionId} className={`flex items-center gap-2 p-2.5 rounded-xl border ${l.enabled ? 'border-violet-200 bg-violet-50/30' : 'border-gray-100'}`}>
                <input
                  type="checkbox"
                  checked={l.enabled}
                  onChange={() => setOptionLines((prev) => prev.map((x) => x.optionId === l.optionId ? { ...x, enabled: !x.enabled } : x))}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{l.label}</p>
                  <p className="text-[10px] text-gray-400">Current: {l.currentExpiryDate || 'None'}</p>
                </div>
                {!clearExpiry && (
                  <input
                    type="text"
                    value={l.expiryDate}
                    onChange={(e) => {
                      const formatted = formatExpiryDateInput(e.target.value);
                      setOptionLines((prev) => prev.map((x) => x.optionId === l.optionId ? { ...x, expiryDate: formatted, enabled: true } : x));
                    }}
                    placeholder="DD/MM/YYYY"
                    className="w-28 py-1.5 px-2 text-xs text-center border border-gray-200 rounded-lg"
                  />
                )}
              </div>
            ))}
          </div>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className={`w-full mt-3 py-2.5 rounded-xl text-sm font-semibold ${canConfirm ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-400'}`}
          >
            {submitting ? 'Saving…' : clearExpiry ? 'Confirm & clear expiry' : 'Confirm & save expiry'}
          </button>
        </>
      )}
    </div>
  );
};

const MultiExpirySetCard = ({ card, onResolved, onClear }) => {
  const { items: initialItems = [], parsedExpiryDate = '', clearExpiry, canEdit } = card;
  const [items, setItems] = useState(() => initialItems.map((i) => ({ ...i, enabled: true })));
  const [bulkDate, setBulkDate] = useState(parsedExpiryDate || '');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    setItems(initialItems.map((i) => ({ ...i, enabled: true })));
    setBulkDate(parsedExpiryDate || '');
  }, [initialItems, parsedExpiryDate]);

  const enabledItems = items.filter((i) => i.enabled);
  const validItems = enabledItems.filter((i) => clearExpiry || !i.expiryDate || isValidExpiryDateFormat(i.expiryDate));
  const canConfirm = canEdit && validItems.length > 0 && !submitting && !result
    && (clearExpiry || validItems.every((i) => i.expiryDate && isValidExpiryDateFormat(i.expiryDate)));

  const applyBulkDate = (raw) => {
    const formatted = formatExpiryDateInput(raw);
    setBulkDate(formatted);
    setItems((prev) => prev.map((i) => (
      i.enabled ? { ...i, expiryDate: formatted } : i
    )));
  };

  const toggleItem = (key) => {
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, enabled: !i.enabled } : i)));
  };

  const updateItemDate = (key, raw) => {
    const formatted = formatExpiryDateInput(raw);
    setItems((prev) => prev.map((i) => (
      i.key === key ? { ...i, expiryDate: formatted, enabled: true } : i
    )));
  };

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      const res = await brainExecuteExpiry({
        items: validItems.map((i) => ({
          productId: i.productId,
          productOptionId: i.productOptionId,
          productName: i.productName,
          optionLabel: i.optionLabel,
          expiryDate: clearExpiry ? '' : i.expiryDate,
          currentExpiryDate: i.currentExpiryDate
        }))
      });
      if (res.code === 200 && res.success) {
        setResult(res.data);
        toast.success(res.message);
        onResolved?.(res.data);
        onClear?.();
      } else {
        toast.error(res.error || res.message);
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="rounded-2xl border border-violet-200 bg-violet-50/60 p-4">
        <FaCheckCircle className="text-violet-600 mb-2" />
        <p className="text-sm font-semibold text-violet-800">{result.updatedCount} expiry date(s) saved</p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-violet-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between gap-2 mb-3">
        <p className="text-sm font-semibold">Multi-product expiry</p>
        <span className="text-[11px] text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full">
          {enabledItems.length} of {items.length} selected
        </span>
      </div>
      {!clearExpiry && (
        <div className="mb-3">
          <label className="text-xs text-gray-500 mb-1 block">Apply DD/MM/YYYY to all selected</label>
          <input
            type="text"
            value={bulkDate}
            onChange={(e) => applyBulkDate(e.target.value)}
            placeholder="DD/MM/YYYY"
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-400"
          />
          {bulkDate && enabledItems.length > 0 && (
            <p className="text-[10px] text-violet-600 mt-1">
              Syncing to {enabledItems.length} selected line(s) as you type
            </p>
          )}
        </div>
      )}
      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
        {items.map((item) => (
          <div
            key={item.key}
            className={`p-2.5 rounded-xl border transition-all ${
              item.enabled
                ? 'border-violet-200 bg-violet-50/40'
                : 'border-gray-100 bg-gray-50 opacity-60'
            }`}
          >
            <div className="flex items-start gap-2">
              <button
                type="button"
                onClick={() => toggleItem(item.key)}
                className={`flex-shrink-0 mt-0.5 w-5 h-5 rounded-md border-2 flex items-center justify-center ${
                  item.enabled ? 'border-violet-500 bg-violet-500 text-white' : 'border-gray-300 bg-white'
                }`}
              >
                {item.enabled && <FaCheckCircle className="text-[10px]" />}
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{item.productName} · {item.optionLabel}</p>
                <p className="text-[10px] text-gray-400 mb-1.5">Current: {item.currentExpiryDate || 'None'}</p>
                {!clearExpiry && (
                  <input
                    type="text"
                    value={item.expiryDate || ''}
                    onChange={(e) => updateItemDate(item.key, e.target.value)}
                    onFocus={() => { if (!item.enabled) toggleItem(item.key); }}
                    placeholder="DD/MM/YYYY"
                    disabled={!item.enabled}
                    className={`w-full py-1.5 px-2 text-xs border rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-400 ${
                      item.enabled ? 'border-gray-200 bg-white' : 'border-gray-100 bg-gray-100 cursor-not-allowed'
                    }`}
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      {!enabledItems.length && (
        <p className="text-[11px] text-amber-600 mt-2">Select at least one option to save.</p>
      )}
      <button
        type="button"
        onClick={handleConfirm}
        disabled={!canConfirm}
        className={`w-full mt-3 py-2.5 rounded-xl text-sm font-semibold transition-all ${
          canConfirm ? 'bg-violet-600 text-white hover:bg-violet-700' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
      >
        {submitting ? 'Saving…' : clearExpiry
          ? `Confirm & clear ${enabledItems.length} line(s)`
          : `Confirm & save ${enabledItems.length} line(s)`}
      </button>
    </div>
  );
};

// ------------------------------------------------------------------
// Thinking loader — animated assistant wait state
// ------------------------------------------------------------------
const BRAIN_THINKING_PHRASES = {
  inventory: [
    'Scanning your catalogue…',
    'Matching products & branches…',
    'Reading stock levels…',
    'Building your action card…'
  ],
  expiry: [
    'Scanning expiry dates…',
    'Matching product options…',
    'Reading shelf-life data…',
    'Preparing your update…'
  ]
};

const BrainThinkingLoader = ({ mode = 'inventory' }) => {
  const isExpiry = mode === 'expiry';
  const phrases = BRAIN_THINKING_PHRASES[mode] || BRAIN_THINKING_PHRASES.inventory;
  const [phraseIdx, setPhraseIdx] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setPhraseIdx((i) => (i + 1) % phrases.length);
    }, 2400);
    return () => clearInterval(timer);
  }, [phrases.length]);

  const ringClass = isExpiry
    ? 'from-violet-400 via-fuchsia-400 to-purple-500'
    : 'from-emerald-400 via-teal-400 to-cyan-500';
  const glowClass = isExpiry ? 'shadow-violet-500/25' : 'shadow-emerald-500/25';
  const textClass = isExpiry ? 'text-violet-700' : 'text-emerald-700';
  const subTextClass = isExpiry ? 'text-violet-500/80' : 'text-emerald-600/80';
  const dotClass = isExpiry ? 'bg-violet-500' : 'bg-emerald-500';

  return (
    <div
      className={`relative overflow-hidden rounded-2xl border bg-white/90 backdrop-blur-sm px-4 py-4 shadow-lg ${glowClass} ${
        isExpiry ? 'border-violet-100' : 'border-emerald-100'
      }`}
      role="status"
      aria-live="polite"
      aria-label={phrases[phraseIdx]}
    >
      <div className={`pointer-events-none absolute inset-0 bg-gradient-to-br opacity-40 ${
        isExpiry ? 'from-violet-50 via-white to-purple-50' : 'from-emerald-50 via-white to-teal-50'
      }`} />

      <motion.div
        className={`pointer-events-none absolute inset-0 rounded-2xl opacity-30 bg-gradient-to-r ${ringClass}`}
        animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
        style={{ backgroundSize: '200% 200%' }}
      />

      <div className="relative flex items-center gap-4">
        <div className="relative flex-shrink-0 w-14 h-14">
          <motion.div
            className="absolute inset-1 rounded-2xl bg-white flex items-center justify-center p-1.5 shadow-md"
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          >
            <BrainLogo className="w-full h-full rounded-lg" alt="Zuntly Brain" />
          </motion.div>

          <motion.div
            className={`absolute inset-0 rounded-2xl border-2 ${isExpiry ? 'border-violet-300/40' : 'border-emerald-300/40'}`}
            animate={{ scale: [1, 1.18, 1], opacity: [0.35, 0, 0.35] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeOut' }}
          />
        </div>

        <div className="flex-1 min-w-0">
          <p className={`text-[11px] font-semibold uppercase tracking-[0.18em] mb-1 ${subTextClass}`}>
            Zuntly Brain
          </p>
          <div className="h-6 overflow-hidden">
            <AnimatePresence mode="wait">
              <motion.p
                key={phraseIdx}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.35 }}
                className={`text-sm font-medium ${textClass}`}
              >
                {phrases[phraseIdx]}
              </motion.p>
            </AnimatePresence>
          </div>

          {/* <div className="flex items-center gap-1.5 mt-2">
            {[0, 1, 2, 3].map((i) => (
              <motion.span
                key={i}
                className={`w-1.5 h-1.5 rounded-full ${dotClass}`}
                animate={{ y: [0, -5, 0], opacity: [0.35, 1, 0.35] }}
                transition={{ duration: 0.7, repeat: Infinity, delay: i * 0.12, ease: 'easeInOut' }}
              />
            ))}
            <span className={`text-[10px] ml-1 ${subTextClass}`}>working magic</span>
          </div> */}
        </div>
      </div>

      <motion.div
        className={`absolute bottom-0 left-0 h-0.5 bg-gradient-to-r ${ringClass}`}
        initial={{ width: '0%' }}
        animate={{ width: ['0%', '72%', '28%', '100%'] }}
        transition={{ duration: 2.8, repeat: Infinity, ease: 'easeInOut' }}
      />
    </div>
  );
};

// ------------------------------------------------------------------
// Chat message renderer
// ------------------------------------------------------------------
const Message = ({ message, brainMode = 'inventory', onResolved, onDistributionDraftChange, onUpdateDraftChange, onSingleUpdateDraftChange, onClearDistribution, onClearUpdate, onRecalculateUpdate, onClearExpiry, catalogHints }) => {
  const isUser = message.role === 'user';
  const cardIntent = message.card?.intent;
  const isExpiryMode = brainMode === 'expiry';
  const isThinking = message.isTyping;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`flex gap-3 mb-4 ${isUser ? 'flex-row-reverse' : 'flex-row'}`}
    >
      <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center shadow-sm overflow-hidden ${
        isUser
          ? 'bg-gradient-to-br from-blue-500 to-blue-600'
          : isThinking
            ? (isExpiryMode
              ? 'bg-white ring-2 ring-violet-200 ring-offset-1 p-0.5'
              : 'bg-white ring-2 ring-emerald-200 ring-offset-1 p-0.5')
            : 'bg-white border border-gray-100 p-0.5'
      }`}>
        {isUser ? (
          <FaSearch className="text-white text-xs" />
        ) : isThinking ? (
          <motion.div
            className="w-full h-full"
            animate={{ rotate: [0, 8, -8, 0], scale: [1, 1.08, 1] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          >
            <BrainLogo className="w-full h-full rounded-lg" alt="Zuntly Brain" />
          </motion.div>
        ) : (
          <BrainLogo className="w-full h-full rounded-lg" alt="Zuntly Brain" />
        )}
      </div>

      <div className={`min-w-0 ${isUser ? 'max-w-[70%]' : 'max-w-[85%] w-full'}`}>
        {message.type === 'card' ? (
          cardIntent === 'distribute'
            ? <DistributionCard card={message.card} onResolved={onResolved} onDraftChange={onDistributionDraftChange} onClear={onClearDistribution} />
            : cardIntent === 'multi_update'
              ? <MultiUpdateCard card={message.card} onResolved={onResolved} onDraftChange={onUpdateDraftChange} onClear={onClearUpdate} onRecalculate={onRecalculateUpdate} chatHint={catalogHints?.multiChatHint} />
              : cardIntent === 'expiry_query'
                ? <ExpiryQueryCard card={message.card} />
                : cardIntent === 'multi_expiry_set'
                  ? <MultiExpirySetCard card={message.card} onResolved={onResolved} onClear={onClearExpiry} />
                  : cardIntent === 'expiry_set'
                    ? <ExpirySetCard card={message.card} onResolved={onResolved} />
                    : cardIntent === 'query'
                      ? <QueryCard card={message.card} />
                      : <InventoryCard card={message.card} onResolved={onResolved} onDraftChange={onSingleUpdateDraftChange} />
        ) : isThinking ? (
          <BrainThinkingLoader mode={brainMode} />
        ) : (
          <div className={`px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
            isUser
              ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-tr-sm'
              : message.isError
                ? 'bg-red-50 text-red-700 border border-red-100 rounded-tl-sm'
                : 'bg-white text-gray-800 border border-gray-100 rounded-tl-sm shadow-sm'
          }`}>
            {message.content}
          </div>
        )}
      </div>
    </motion.div>
  );
};

// ------------------------------------------------------------------
// Main
// ------------------------------------------------------------------
const ZuntlyBrain = () => {
  const [mode, setMode] = useState(() => readStoredBrainMode());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [distributionDraft, setDistributionDraft] = useState(null);
  const [updateDraft, setUpdateDraft] = useState(null);
  const [singleUpdateDraft, setSingleUpdateDraft] = useState(null);
  const [catalogHints, setCatalogHints] = useState(FALLBACK_CATALOG_HINTS);
  const [expiryHints, setExpiryHints] = useState(FALLBACK_EXPIRY_HINTS);
  const [expiryQueryContext, setExpiryQueryContext] = useState(null);
  const [dailyUsage, setDailyUsage] = useState(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const distributionCardIdRef = useRef(null);
  const updateCardIdRef = useRef(null);
  const singleUpdateCardIdRef = useRef(null);
  const expiryCardIdRef = useRef(null);

  const activeMode = MODES.find((m) => m.id === mode);
  const activeHints = mode === 'expiry' ? expiryHints : catalogHints;
  const isDailyLimitReached = !!(dailyUsage?.limited && dailyUsage?.exhausted);

  const refreshDailyUsage = useCallback(async () => {
    try {
      const res = await brainGetDailyUsage();
      if (res.code === 200 && res.success) {
        setDailyUsage(res.data || null);
      }
    } catch {
      /* keep previous usage */
    }
  }, []);

  useEffect(() => {
    refreshDailyUsage();
  }, [refreshDailyUsage]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (mode === 'expiry') {
          const res = await brainGetExpiryExamples();
          if (!cancelled && res.code === 200 && res.success && res.data?.examples?.length) {
            setExpiryHints({ ...FALLBACK_EXPIRY_HINTS, ...res.data });
          }
        } else {
          const res = await brainGetExamples();
          if (!cancelled && res.code === 200 && res.success && res.data?.examples?.length) {
            setCatalogHints({ ...FALLBACK_CATALOG_HINTS, ...res.data });
          }
        }
      } catch {
        /* keep fallback hints */
      }
    })();
    return () => { cancelled = true; };
  }, [mode]);

  const clearDistributionDraft = useCallback(() => {
    setDistributionDraft(null);
    distributionCardIdRef.current = null;
    setMessages((prev) => prev.filter((m) => m.type !== 'card' || m.card?.intent !== 'distribute'));
  }, []);

  const clearUpdateDraft = useCallback(() => {
    setUpdateDraft(null);
    updateCardIdRef.current = null;
    setMessages((prev) => prev.filter((m) => m.type !== 'card' || m.card?.intent !== 'multi_update'));
  }, []);

  const clearSingleUpdateDraft = useCallback(() => {
    setSingleUpdateDraft(null);
    singleUpdateCardIdRef.current = null;
  }, []);

  const clearExpiryDraft = useCallback(() => {
    expiryCardIdRef.current = null;
    setMessages((prev) => prev.filter((m) => m.type !== 'card' || !['expiry_set', 'multi_expiry_set'].includes(m.card?.intent)));
  }, []);

  const upsertExpiryCard = useCallback((cardData, assistantText) => {
    const cardId = expiryCardIdRef.current || `expiry-card-${Date.now()}`;
    expiryCardIdRef.current = cardId;
    setMessages((prev) => {
      const withoutOld = prev.filter((m) => m.id !== cardId);
      const textId = `${cardId}-text`;
      const withoutText = withoutOld.filter((m) => m.id !== textId);
      return [
        ...withoutText,
        ...(assistantText ? [{ id: textId, role: 'assistant', type: 'text', content: assistantText }] : []),
        { id: cardId, role: 'assistant', type: 'card', card: cardData }
      ];
    });
  }, []);

  const handleModeChange = (nextMode) => {
    if (!nextMode.live || nextMode.id === mode) return;
    setMode(nextMode.id);
    persistBrainMode(nextMode.id);
    setMessages([]);
    setInput('');
    clearDistributionDraft();
    clearUpdateDraft();
    clearSingleUpdateDraft();
    clearExpiryDraft();
    setExpiryQueryContext(null);
  };

  const upsertSingleUpdateCard = useCallback((cardData, assistantText) => {
    const enriched = enrichSingleUpdateCard(cardData);
    const cardId = singleUpdateCardIdRef.current || `single-card-${Date.now()}`;
    singleUpdateCardIdRef.current = cardId;
    setSingleUpdateDraft(enriched);

    setMessages((prev) => {
      const withoutOld = prev.filter((m) => m.id !== cardId);
      const textId = `${cardId}-text`;
      const withoutText = withoutOld.filter((m) => m.id !== textId);
      return [
        ...withoutText,
        ...(assistantText ? [{ id: textId, role: 'assistant', type: 'text', content: assistantText }] : []),
        { id: cardId, role: 'assistant', type: 'card', card: { ...enriched, intent: 'update' } }
      ];
    });
  }, []);

  const upsertDistributionCard = useCallback((cardData, assistantText) => {
    const cardId = distributionCardIdRef.current || `dist-card-${Date.now()}`;
    distributionCardIdRef.current = cardId;
    setDistributionDraft(cardData);

    setMessages((prev) => {
      const withoutOld = prev.filter((m) => m.id !== cardId);
      const textId = `${cardId}-text`;
      const withoutText = withoutOld.filter((m) => m.id !== textId);
      return [
        ...withoutText,
        ...(assistantText ? [{ id: textId, role: 'assistant', type: 'text', content: assistantText }] : []),
        { id: cardId, role: 'assistant', type: 'card', card: cardData }
      ];
    });
  }, []);

  const upsertUpdateCard = useCallback((cardData, assistantText) => {
    const cardId = updateCardIdRef.current || `update-card-${Date.now()}`;
    updateCardIdRef.current = cardId;
    setUpdateDraft(cardData);

    setMessages((prev) => {
      const withoutOld = prev.filter((m) => m.id !== cardId);
      const textId = `${cardId}-text`;
      const withoutText = withoutOld.filter((m) => m.id !== textId);
      return [
        ...withoutText,
        ...(assistantText ? [{ id: textId, role: 'assistant', type: 'text', content: assistantText }] : []),
        { id: cardId, role: 'assistant', type: 'card', card: cardData }
      ];
    });
  }, []);

  const handleDistributionDraftChange = useCallback((draft) => {
    setDistributionDraft(draft);
  }, []);

  const handleUpdateDraftChange = useCallback((draft) => {
    setUpdateDraft(draft);
  }, []);

  const handleSingleUpdateDraftChange = useCallback((draft) => {
    setSingleUpdateDraft(draft);
  }, []);

  const recalculateUpdateStock = useCallback(async (items, branchId, quantityMode) => {
    const res = await brainResolveInventory({
      message: 'update',
      updateRecalculate: true,
      existingUpdateItems: items,
      existingUpdateBranchId: branchId,
      quantityMode
    });
    if (res.code === 200 && res.success) return res.data;
    throw new Error(res.message || 'Recalculate failed');
  }, []);

  const scrollToBottom = useCallback(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);
  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const send = async (text = input) => {
    const content = (text || '').trim().replace(/^\++\s*/, '').trim();
    if (!content || loading) return;

    if (!activeMode?.live) {
      toast.info('This mode is coming soon.');
      return;
    }

    if (isDailyLimitReached) {
      toast.error(
        dailyUsage?.resetAtDisplay
          ? `Daily limit reached (${dailyUsage.used}/${dailyUsage.limit}). Resets on ${dailyUsage.resetAtDisplay} IST.`
          : 'Daily request limit reached. Try again after midnight IST.'
      );
      return;
    }

    const userMsg = { id: Date.now(), role: 'user', type: 'text', content };
    const typingId = 'typing-' + Date.now();
    setMessages((prev) => [...prev, userMsg, { id: typingId, role: 'assistant', type: 'text', content: '', isTyping: true }]);
    setInput('');
    setLoading(true);
    if (inputRef.current) inputRef.current.style.height = '48px';

    try {
      if (mode === 'expiry') {
        const res = await brainResolveExpiry({
          message: content,
          previousQueryItems: expiryQueryContext?.length ? expiryQueryContext : undefined
        });
        setMessages((prev) => prev.filter((m) => m.id !== typingId));

        if (res.code === 429) {
          if (res.data?.dailyUsage) setDailyUsage(res.data.dailyUsage);
          setMessages((prev) => [...prev, { id: Date.now() + 1, role: 'assistant', type: 'text', content: res.message || 'Daily limit reached.', isError: true }]);
        } else if (res.code === 200 && res.success) {
          const data = res.data || {};
          if (['expiry_query', 'expiry_set', 'multi_expiry_set'].includes(data.intent)) {
            if (data.intent === 'expiry_query') {
              if (data.items?.length) setExpiryQueryContext(data.items);
              setMessages((prev) => [
                ...prev,
                { id: Date.now() + 1, role: 'assistant', type: 'text', content: res.message },
                { id: Date.now() + 2, role: 'assistant', type: 'card', card: data }
              ]);
            } else {
              upsertExpiryCard(data, res.message);
            }
          } else {
            setMessages((prev) => [...prev, { id: Date.now() + 1, role: 'assistant', type: 'text', content: res.message }]);
          }
        } else {
          setMessages((prev) => [...prev, { id: Date.now() + 1, role: 'assistant', type: 'text', content: res.message || 'Something went wrong.', isError: true }]);
        }
        return;
      }

      const freshCommand = isFreshInventoryCommand(content);
      if (freshCommand) clearSingleUpdateDraft();

      const appendDistribution = !!distributionDraft && !isDistributeKeyword(content) && !freshCommand;
      const appendMultiUpdate = !!updateDraft && !appendDistribution && !isDistributeKeyword(content) && !freshCommand;
      const appendSingleUpdate = !!singleUpdateDraft && !appendDistribution && !appendMultiUpdate && !freshCommand;

      const res = await brainResolveInventory({
        message: content,
        distributionAppend: appendDistribution || undefined,
        existingItems: appendDistribution ? distributionDraft.items : undefined,
        existingProductGroups: appendDistribution ? distributionDraft.productGroups : undefined,
        existingTargetBranchId: appendDistribution ? distributionDraft.targetBranchId : undefined,
        updateAppend: appendMultiUpdate || undefined,
        existingUpdateItems: appendMultiUpdate ? updateDraft.items : undefined,
        existingUpdateBranchId: appendMultiUpdate ? updateDraft.branchId : undefined,
        quantityMode: appendMultiUpdate ? updateDraft.quantityMode : undefined,
        inventoryAction: appendMultiUpdate ? updateDraft.inventoryAction : undefined,
        singleUpdateAppend: appendSingleUpdate || undefined,
        existingSingleUpdate: appendSingleUpdate ? singleUpdateDraft : undefined
      });
      setMessages((prev) => prev.filter((m) => m.id !== typingId));

      if (res.code === 429) {
        if (res.data?.dailyUsage) setDailyUsage(res.data.dailyUsage);
        setMessages((prev) => [...prev, { id: Date.now() + 1, role: 'assistant', type: 'text', content: res.message || 'Daily limit reached.', isError: true }]);
      } else if (res.code === 200 && res.success) {
        const data = res.data || {};

        if (data.intent === 'distribute' && (data.items?.length > 0 || data.productGroups?.length || distributionDraft)) {
          clearUpdateDraft();
          clearSingleUpdateDraft();
          if (data.chatOnly) {
            setMessages((prev) => [...prev, { id: Date.now() + 1, role: 'assistant', type: 'text', content: res.message }]);
          } else {
            const mergedCard = {
              ...data,
              intent: 'distribute',
              items: data.items?.length ? data.items : distributionDraft?.items || [],
              productGroups: data.productGroups?.length ? data.productGroups : distributionDraft?.productGroups || [],
              targetBranchId: data.targetBranchId || distributionDraft?.targetBranchId || null
            };
            upsertDistributionCard(mergedCard, res.message);
          }
        } else if (appendDistribution && distributionDraft) {
          upsertDistributionCard(distributionDraft, res.message || "Couldn't add from chat — use the checkboxes or search bar.");
        } else if (data.intent === 'multi_update' && (data.items != null || updateDraft)) {
          clearDistributionDraft();
          clearSingleUpdateDraft();
          if (data.chatOnly) {
            setMessages((prev) => [...prev, { id: Date.now() + 1, role: 'assistant', type: 'text', content: res.message }]);
          } else {
            const mergedCard = {
              ...updateDraft,
              ...data,
              intent: 'multi_update',
              items: data.items ?? updateDraft?.items ?? [],
              branchId: data.branchId || updateDraft?.branchId || null,
              quantityMode: data.quantityMode || updateDraft?.quantityMode || 'add_each',
              inventoryAction: data.inventoryAction || updateDraft?.inventoryAction
            };
            upsertUpdateCard(mergedCard, res.message);
          }
        } else if (appendSingleUpdate && (data.candidates?.length || singleUpdateDraft)) {
          if (data.candidates?.length) {
            upsertSingleUpdateCard({ ...singleUpdateDraft, ...data, intent: 'update' }, res.message);
          } else {
            setMessages((prev) => [...prev, { id: Date.now() + 1, role: 'assistant', type: 'text', content: res.message }]);
          }
        } else if (appendMultiUpdate && updateDraft) {
          upsertUpdateCard(updateDraft, res.message || "Couldn't add from chat — use the search bar to pick the exact option.");
        } else if (data.intent === 'query' && data.candidates?.length > 0) {
          clearDistributionDraft();
          clearUpdateDraft();
          clearSingleUpdateDraft();
          setMessages((prev) => [
            ...prev,
            { id: Date.now() + 1, role: 'assistant', type: 'text', content: res.message },
            { id: Date.now() + 2, role: 'assistant', type: 'card', card: data }
          ]);
        } else if (data.candidates && data.candidates.length > 0) {
          clearDistributionDraft();
          clearUpdateDraft();
          upsertSingleUpdateCard({ ...data, intent: 'update' }, res.message);
        } else if (appendSingleUpdate && singleUpdateDraft) {
          setMessages((prev) => [...prev, { id: Date.now() + 1, role: 'assistant', type: 'text', content: res.message || "I couldn't match that option. Try e.g. 500gm or add 200gm 5." }]);
        } else {
          setMessages((prev) => [...prev, { id: Date.now() + 1, role: 'assistant', type: 'text', content: res.message }]);
        }
      } else {
        setMessages((prev) => [...prev, { id: Date.now() + 1, role: 'assistant', type: 'text', content: res.message || 'Something went wrong.', isError: true }]);
      }
    } catch (e) {
      setMessages((prev) => prev.filter((m) => m.id !== typingId));
      if (e?.statusCode === 429 && e?.errorData) {
        if (e.errorData.data?.dailyUsage) setDailyUsage(e.errorData.data.dailyUsage);
        setMessages((prev) => [...prev, {
          id: Date.now() + 1,
          role: 'assistant',
          type: 'text',
          content: e.errorData.message || 'Daily limit reached.',
          isError: true
        }]);
      } else {
        setMessages((prev) => [...prev, { id: Date.now() + 1, role: 'assistant', type: 'text', content: e?.message || "I couldn't process that. Please try again.", isError: true }]);
      }
    } finally {
      setLoading(false);
      refreshDailyUsage();
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh)] bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-shrink-0">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg shadow-emerald-500/15 border border-gray-100 p-1">
                <BrainLogo className="w-full h-full rounded-lg" alt="Zuntly Brain" />
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-400 rounded-full border-2 border-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                Zuntly Brain
              </h1>
              <p className="text-xs text-gray-500">Type a command — the Brain does the rest</p>
            </div>
            {dailyUsage?.limited && (
              <div
                className={`ml-auto flex flex-col items-end gap-0.5 px-3 py-2 rounded-xl border text-xs ${
                  isDailyLimitReached
                    ? 'bg-amber-50 border-amber-200 text-amber-800'
                    : 'bg-gray-50 border-gray-200 text-gray-600'
                }`}
              >
                <div className="flex items-center gap-1.5 font-semibold">
                  <FaClock size="0.75em" />
                  <span>{dailyUsage.used}/{dailyUsage.limit} requests today</span>
                </div>
                {/* {isDailyLimitReached ? (
                  <p className="text-[11px] text-amber-700 text-right max-w-[220px] leading-snug">
                    Daily limit reached. Resets on {dailyUsage.resetAtDisplay} IST.
                  </p>
                ) : (
                  <p className="text-[10px] text-gray-400">
                    Resets daily at 12:00 AM IST
                  </p>
                )} */}
              </div>
            )}
          </div>

          {/* Mode pills */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {MODES.map((m) => (
              <button
                key={m.id}
                onClick={() => handleModeChange(m)}
                disabled={!m.live}
                className={`flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                  mode === m.id
                    ? (m.id === 'expiry'
                      ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow'
                      : 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow')
                    : m.live
                      ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                }`}
              >
                {m.icon}
                {m.label}
                {!m.live && <span className="text-[9px] uppercase tracking-wide ml-0.5">soon</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Conversation */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-3xl mx-auto">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center pt-10">
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-4 shadow-xl ${
                mode === 'expiry'
                  ? 'bg-gradient-to-br from-violet-500 to-purple-600 shadow-violet-500/25'
                  : 'bg-gradient-to-br from-emerald-500 to-teal-600 shadow-emerald-500/25'
              }`}>
                {mode === 'expiry' ? <FaCalendarAlt className="text-white text-2xl" /> : <FaBoxes className="text-white text-2xl" />}
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-1">
                {mode === 'expiry' ? 'Product expiry, in one line' : 'Inventory, in one line'}
              </h2>
              <p className="text-gray-500 max-w-md text-sm mb-6">
                {mode === 'expiry' ? (
                  <>
                    <b>Set</b> or <b>clear</b> expiry dates per product option, or ask <b>what expires in N days</b> / <b>what is already expired</b>.
                    Tap an example below — built from your real products.
                  </>
                ) : (
                  <>
                    <b>Add</b> stock, <b>Remove</b> stock, <b>Update</b> stock, <b>check</b> availability, or <b>distribute</b> from warehouse to branches.
                    Tap an example below — built from your real products and branches.
                  </>
                )}
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                {activeHints.examples.map((ex) => (
                  <button
                    key={ex}
                    type="button"
                    onClick={() => send(ex)}
                    className={`px-3 py-2 rounded-xl text-xs font-medium bg-white border border-gray-200 text-gray-600 transition-all text-left max-w-[min(100%,22rem)] whitespace-normal leading-snug ${
                      mode === 'expiry' ? 'hover:border-violet-300 hover:text-violet-600' : 'hover:border-emerald-300 hover:text-emerald-600'
                    }`}
                    title={ex}
                  >
                    “{ex}”
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <AnimatePresence initial={false}>
              {messages.map((m) => (
                <Message
                  key={m.id}
                  message={m}
                  brainMode={mode}
                  onResolved={scrollToBottom}
                  onDistributionDraftChange={handleDistributionDraftChange}
                  onUpdateDraftChange={handleUpdateDraftChange}
                  onSingleUpdateDraftChange={handleSingleUpdateDraftChange}
                  onClearDistribution={clearDistributionDraft}
                  onClearUpdate={clearUpdateDraft}
                  onRecalculateUpdate={recalculateUpdateStock}
                  onClearExpiry={clearExpiryDraft}
                  catalogHints={catalogHints}
                />
              ))}
            </AnimatePresence>
          )}
          <div ref={endRef} />
        </div>
      </div>

      {/* Input */}
      <div className="bg-white border-t border-gray-200 px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-end gap-3">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = Math.min(e.target.scrollHeight, 160) + 'px';
            }}
            onKeyDown={handleKey}
            placeholder={
              mode === 'expiry'
                ? expiryHints.placeholder
                : distributionDraft
                  ? catalogHints.distributeDraftPlaceholder
                  : updateDraft
                    ? catalogHints.updateDraftPlaceholder
                    : singleUpdateDraft
                      ? catalogHints.singleDraftPlaceholder
                      : catalogHints.placeholder
            }
            rows={1}
            disabled={loading || !activeMode?.live || isDailyLimitReached}
            className={`flex-1 px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:border-transparent text-sm disabled:opacity-60 disabled:cursor-not-allowed ${
              mode === 'expiry' ? 'focus:ring-violet-500' : 'focus:ring-emerald-500'
            }`}
            style={{ minHeight: '48px', maxHeight: '160px' }}
          />
          <button
            onClick={() => send()}
            disabled={!input.trim() || loading || isDailyLimitReached}
            className={`p-3 rounded-xl transition-all ${
              input.trim() && !loading
                ? (mode === 'expiry'
                  ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/25 hover:scale-105'
                  : 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/25 hover:scale-105')
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            {loading ? <FaSpinner className="animate-spin" size={18} /> : <IoSend size={18} />}
          </button>
        </div>
        {isDailyLimitReached && (
          <p className="text-xs text-amber-700 mt-2 text-center max-w-3xl mx-auto">
            You have used all {dailyUsage.limit} Brain requests for today. Limit resets on {dailyUsage.resetAtDisplay} IST.
          </p>
        )}
        <p className="text-[10px] text-gray-400 mt-2 text-center max-w-3xl mx-auto">
          Zuntly Brain uses AI to understand your command, then matches against your real catalogue. Always confirm before updating.
        </p>
      </div>
    </div>
  );
};

export default ZuntlyBrain;
